/**
 * Entry point. Boots the world, loads the data mirror, and runs the
 * Phase 3 skirmish: settlement loop (Phase 2) + training + data-driven
 * combat + a Samnite camp AI + victory/defeat.
 */
import * as THREE from "three";
// Theme (theme.css) is imported first so the loading screen — the first
// DOM painted — is already styled when boot() runs.
import "./ui/theme.css";
import { GameDB } from "./data/gamedb";
import { SimLoop } from "./sim/loop";
import { Economy } from "./sim/economy";
import {
  costOf,
  footprintOf,
  missingRequirements,
  overlaps,
  placeBuilding,
  tickConstruction,
  type PlacedBuilding,
} from "./sim/buildings";
import {
  dealBuildingDamage,
  dealMeleeDamage,
  inBuildingRange,
  inMeleeRange,
  makeCombatant,
  statNum,
  tickMorale,
  type BuildingTarget,
  type Combatant,
  type Team,
} from "./sim/combat";
import {
  bestTierForFaction,
  formationUpkeepPerMin,
  musterTiers,
  resolvePaperStrength,
  tickFormationUpkeep,
  tierById,
  tierTrainCost,
  trainScale,
  type OrgTier,
} from "./sim/organization";
import { TrainingQueues, trainableUnits, unitCost } from "./sim/training";
import {
  createResearch,
  effectSummary,
  TechEffects,
  tickResearch,
} from "./sim/research";
import { createEchelon } from "./sim/echelon";
import { canTrain, commandStatus, consumesCommand } from "./sim/command";
import { AiDirector, type AiCallbacks } from "./sim/ai";
import { DIFFICULTIES, type DifficultyId } from "./sim/difficulty";
import { createWorld } from "./render/world";
import { RtsCamera } from "./render/camera";
import { Selection } from "./render/selection";
import { TouchControls, type SelectRect } from "./render/touch";
import {
  createFormationView,
  formationSpeed,
  noteMeleeContact,
  orderFormationMove,
  setFormationCount,
  updateFormationAnim,
  updateFormationLOD,
  updateFormationMovement,
  DEFENSIVE_FORMATION_IDS,
  type MarchContext,
} from "./render/units";
import { AnimSystem, loadRealClipsForUnits } from "./render/anim";
import { ModelStore } from "./render/models";
import { initIcons } from "./ui/icons";
import {
  MEDALLION_PX,
  MedallionRenderer,
  type MedallionSpec,
} from "./render/medallions";
import { artKeyFor } from "./data/artmap";
import { VolleyField } from "./render/volley";
import { RoadNetwork, roadsFromBuildings } from "./sim/roads";
import {
  createBuildingView,
  refreshBuildingArt,
  highlightBuilding,
  updateBuildingView,
  type BuildingView,
} from "./render/buildings";
import { Hud } from "./ui/hud";
import { BuildMenu } from "./ui/buildmenu";
import { TrainPanel } from "./ui/trainpanel";
import { Banner } from "./ui/banner";
import { ResearchPanel } from "./ui/researchpanel";
import { chooseFaction } from "./ui/factionselect";
import type { FactionChoice } from "./ui/factionselect";
import { LoadingScreen } from "./ui/loadingscreen";
import { MainMenu } from "./ui/mainmenu";
import { SettingsScreen } from "./ui/settingsscreen";
import { PauseMenu } from "./ui/pausemenu";
import { MenuFlow } from "./ui/menuflow";
import { settings } from "./ui/settings";
import { reserveNodeId, scatterNodes } from "./sim/resources";
import {
  farmResource,
  reserveWorkerId,
  Workforce,
  workforceUpkeep,
} from "./sim/workers";
import { NodeField } from "./render/nodes";
import { WorkerField } from "./render/workers";
import { HealthBars } from "./render/healthbars";
import { Minimap } from "./ui/minimap";
import { SpeedControl } from "./ui/speedcontrol";
import { VisionSystem, type VisionViewer } from "./sim/vision";
import { DayNightCycle } from "./sim/daynight";
import { NavSystem, NAV, separationPush } from "./sim/nav";
import { MAP_SIZES, isMapSizeId, type MapSizeId } from "./sim/mapconfig";
import { SpatialHash } from "./sim/spatial";
import { FogOfWarRenderer } from "./render/fog";
import { BrazierLights, TorchField } from "./render/torches";
import { BRAZIER_ID } from "./data/prototype_overlay";
import type { BuildingDef, FactionDef, UnitDef } from "./data/types";
import type { Worker, WorkerState } from "./sim/workers";
import type { NodeKind } from "./sim/resources";

/**
 * Skirmish map size (Small / Standard / Large) — chosen on the faction
 * select screen, defaulting to Small on touch/narrow devices. Standard
 * preserves the exact classic tuning (half-extent 200).
 */

async function boot(): Promise<void> {
  const container = document.getElementById("app")!;

  // Loading screen shows before anything else initializes, with honest
  // phase labels as each subsystem comes up.
  const loading = new LoadingScreen();
  loading.setPhase("icons");
  // UI icon index (vendored SVGs): loaded before any panel renders so
  // missing icons degrade to text labels without 404 console errors.
  await initIcons(import.meta.env.BASE_URL);

  loading.setPhase("data");
  const db = await GameDB.load();
  if (db.warnings.length > 0) {
    console.info(`[gamedb] loaded with ${db.warnings.length} warning(s)`);
  }
  loading.setPhase("models");
  const factionFallback: FactionDef = {
    id: "rome",
    name_en: "Rome",
    playable: true,
    implemented: true,
    palette: {
      primary: "#8c2b23",
      secondary: "#c9bfae",
      metal: "#6f7b86",
      accent: "#b98b3a",
    },
    starting_units: {},
    starting_buildings: {},
    starting_resources: { food: 400, timber: 200, stone: 100 },
  };
  // Skeletal animation + medallions are created up here so the faction
  // cards can show each signature unit's animated portrait. AnimSystem's
  // constructor is pure (registers placeholder clips; the bone atlas is
  // just data until first render).
  const animSys = new AnimSystem();
  // ModelStore is created up here too: medallions resolve vendored GLBs
  // through it, and the real-clip sweep below needs it at boot.
  const modelStore = new ModelStore();
  const medallions = new MedallionRenderer(animSys, modelStore);
  loading.setPhase("world");
  await loading.done();

  // ---- Main menu flow: menu -> skirmish setup -> game ----
  const flow = new MenuFlow();
  flow.send({ type: "loading-done" }); // boot -> loading
  flow.send({ type: "loading-done" }); // loading -> menu
  const menu = new MainMenu({
    onSkirmish: () => flow.send({ type: "menu-skirmish" }),
    onSettings: () => flow.send({ type: "menu-settings" }),
  });
  const settingsScreen = new SettingsScreen(() =>
    flow.send({ type: "settings-back" }),
  );
  flow.onChange((s) => {
    if (s === "menu") menu.show();
    else menu.hide();
    if (s === "settings") settingsScreen.show();
    else settingsScreen.hide();
  });
  /** Resolve when the player presses Skirmish on the main menu. */
  const waitSkirmish = (): Promise<void> =>
    new Promise((res) => {
      const off = flow.onChange((s) => {
        if (s === "setup") {
          off();
          res();
        }
      });
    });
  // Setup loop: backing out of faction select returns to the menu.
  // Difficulty is `let`: loading a save restores the difficulty it was
  // saved with.
  let choice: FactionChoice | null = null;
  while (!choice) {
    menu.show();
    await waitSkirmish();
    menu.hide();
    choice = await chooseFaction([...db.factions.values()], {
      db,
      medallions,
      onBack: () => flow.send({ type: "setup-back" }),
    });
  }
  flow.send({ type: "setup-start" });
  menu.destroy();
  settingsScreen.destroy();

  const { factionId: playerFactionId, mapSize: mapSizeId } = choice;
  let { difficulty } = choice;
  const mapSizeDef = MAP_SIZES[mapSizeId];
  // The 3D world is created after faction select so terrain, fog UVs,
  // nav/vision grids, and camera bounds all match the chosen map size.
  const world = createWorld(container, { halfExtent: mapSizeDef.halfExtent });
  const camera = new RtsCamera(world.camera, world.renderer.domElement, world.groundY);
  camera.setBounds(mapSizeDef.halfExtent);
  // Camera preferences come from the shared settings store (Settings
  // screen and skirmish setup toggle write the same store), applied live.
  const applyCameraSettings = (): void => {
    const s = settings.get();
    camera.invertPan = s.cameraInvert;
    camera.panSpeed = s.cameraSpeed;
  };
  applyCameraSettings();
  settings.onChange(applyCameraSettings);
  // Starting sites: the classic bearings (player at origin, enemy camp at
  // (-130,-70) normalized × baseDistance) are snapped to buildable ground —
  // flat, dry, and clear of the map edge — via a deterministic golden-angle
  // spiral over the shared Heightfield. Same seed+size -> same sites, no RNG.
  const field = world.env.field;
  const findBaseSite = (cx: number, cz: number): { x: number; z: number } => {
    const margin = 30;
    const clear = (x: number, z: number): boolean => {
      if (Math.abs(x) > mapSizeDef.halfExtent - margin) return false;
      if (Math.abs(z) > mapSizeDef.halfExtent - margin) return false;
      // A base needs a dry, gentle disc ~28u across for its buildings.
      for (let a = 0; a < 8; a++) {
        const px = x + Math.cos((a / 8) * Math.PI * 2) * 14;
        const pz = z + Math.sin((a / 8) * Math.PI * 2) * 14;
        if (field.isWaterAt(px, pz) || field.slopeAt(px, pz) > 0.35) return false;
      }
      return true;
    };
    if (clear(cx, cz)) return { x: cx, z: cz };
    const maxR = mapSizeDef.baseDistance * 0.4;
    for (let i = 1; i <= 600; i++) {
      const r = 4 * Math.sqrt(i);
      if (r > maxR) break;
      const a = i * 2.399963;
      const x = cx + Math.cos(a) * r;
      const z = cz + Math.sin(a) * r;
      if (clear(x, z)) return { x, z };
    }
    return { x: cx, z: cz }; // fallback: the classic spot, whatever the ground
  };
  const PLAYER_BASE = findBaseSite(0, 0);
  /**
   * Snap a desired spawn point to the nearest dry, reasonably flat ground.
   * Deterministic spiral search (no RNG): guarantees starting units never
   * spawn in water or on a cliff, even when their formation offset lands
   * outside the base site's validated disc.
   */
  const snapToGround = (x: number, z: number): { x: number; z: number } => {
    if (!field.isWaterAt(x, z) && field.slopeAt(x, z) <= 0.55) return { x, z };
    for (let i = 1; i <= 200; i++) {
      const r = 2 * Math.sqrt(i);
      if (r > 40) break;
      const a = i * 2.399963;
      const px = x + Math.cos(a) * r;
      const pz = z + Math.sin(a) * r;
      if (Math.abs(px) > mapSizeDef.halfExtent - 10) continue;
      if (Math.abs(pz) > mapSizeDef.halfExtent - 10) continue;
      if (!field.isWaterAt(px, pz) && field.slopeAt(px, pz) <= 0.55) {
        return { x: px, z: pz };
      }
    }
    return { x, z }; // fallback: requested spot (shouldn't happen)
  };
  // Enemy camp keeps its classic bearing (-130,-70 normalized), placed at
  // the map size's base distance from the player's base, snapped to ground.
  const CAMP = (() => {
    const dx = -130;
    const dz = -70;
    const len = Math.hypot(dx, dz);
    return findBaseSite(
      (dx / len) * mapSizeDef.baseDistance,
      (dz / len) * mapSizeDef.baseDistance,
    );
  })();
  const playerFaction = db.factions.get(playerFactionId) ?? {
    ...factionFallback,
    id: playerFactionId,
  };
  const enemyFactionId = playerFactionId === "rome" ? "samnite" : "rome";
  const enemyFaction = db.factions.get(enemyFactionId) ?? {
    ...factionFallback,
    id: enemyFactionId,
    name_en: enemyFactionId,
  };
  /** Command building id = the faction's starting civic building (data). */
  const noStarts: Record<string, number> = {};
  const playerCommandId = Object.keys(playerFaction.starting_buildings ?? noStarts)[0] ?? "forum";
  const enemyCommandId = Object.keys(enemyFaction.starting_buildings ?? noStarts)[0] ?? "forum";
  const playerCommandName =
    db.buildings.get(playerCommandId)?.name_en ?? "command building";
  const enemyCommandName =
    db.buildings.get(enemyCommandId)?.name_en ?? "command building";
  const economy = new Economy(db, playerFaction.starting_resources ?? noStarts);
  const hud = new Hud(db, economy, playerFaction);
  const banner = new Banner();
  const research = createResearch();
  const echelonState = createEchelon();
  const techFx = new TechEffects(research, db);
  /** Strategic points: earned by destroying enemy formations in battle. */
  let strategicPoints = 0;

  // --- Resource nodes + workforce ---
  // Node budget and scatter area scale with the skirmish map size.
  const nodes = scatterNodes({
    halfExtent: mapSizeDef.halfExtent,
    playerBase: { x: PLAYER_BASE.x, z: PLAYER_BASE.z },
    enemyBase: { x: CAMP.x, z: CAMP.z },
    clearRadius: Math.max(27, 45 * (mapSizeDef.halfExtent / 200)),
    timber: mapSizeDef.nodeTimber,
    stone: mapSizeDef.nodeStone,
    terrain: field,
  });
  const nodeField = new NodeField(world.groundY);
  nodeField.sync(nodes);
  world.scene.add(nodeField.group);
  const workforce = new Workforce();
  // (animSys + medallions created above the faction select so cards can
  // show signature-unit medallions.)
  // Real-clip sweep: every unit type with a vendored GLB loads its embedded
  // clips in the background (faber first among them); same-named slots
  // replace procedural placeholders. Never blocks boot, never rejects.
  void loadRealClipsForUnits(
    animSys,
    db.units.values(),
    (id) => db.unitModel(id),
    modelStore,
  ).then((replaced) => {
    if (replaced.length > 0) {
      console.info(`[anim] real clips loaded: ${replaced.join(", ")}`);
    }
  });
  const workerField = new WorkerField(world.scene, animSys);
  // Real faber geometry: rebind the vendored faber.glb onto the worker
  // rig in the background; the procedural humanoid renders until it lands.
  workerField.attachRealGeometry(db.unitModel("faber"), modelStore);
  world.selectables.add(workerField.mesh);
  const healthBars = new HealthBars(world.scene);
  const minimap = new Minimap(mapSizeDef.worldSize);

  // --- Fog of war + day/night (feature/fog-of-war) ---
  // Both teams share the same human-like sight rules (range, hill/forest
  // occlusion, elevation, night). The vision system above drives rendering
  // and player visibility; the AI keeps its own data-vision knowledge
  // model (see AiDirector: a player building is "seen" when an AI group
  // comes within that unit's data `vision` range).
  const vision = new VisionSystem({
    groundY: world.groundY,
    worldSize: mapSizeDef.worldSize,
  });
  vision.setTreeNodes(nodes);

  // --- Flow-field navigation: one shared grid per team+goal. ---
  // Formation anchors are the pathing agents; soldiers keep slot offsets
  // around the moving anchor (see sim/nav.ts + render/units.ts).
  // Forest slows movement: timber splats reuse vision.ts's sigma/cutoff.
  const nav = new NavSystem({
    heightAt: world.groundY,
    // Rivers and the lake are impassable: block nav cells under open water.
    isBlockedAt: (x, z) => field.isWaterAt(x, z),
    tuning: { ...NAV, worldSize: mapSizeDef.worldSize },
  });
  const syncNavForest = (): void => {
    nav.setForestSplats(
      nodes
        .filter((n) => n.kind === "timber" && n.amount > 0)
        .map((n) => ({ x: n.x, z: n.z })),
    );
  };
  syncNavForest();

  // --- Roads: the road-data contract (sim/roads.ts). Active is_path
  // buildings sync into rects; the map generator will fill rects directly.
  // The nav bakes 1/bonus traversal cost (route preference), formations
  // narrow to 4-abreast on roads and march faster (data bonuses).
  const roads = new RoadNetwork();
  let roadKey = "";
  /** Debug hooks lock roads (map-generator contract) so syncRoads won't. */
  let roadsLocked = false;
  const syncRoads = (): void => {
    if (roadsLocked) return;
    const rects = roadsFromBuildings(buildings);
    const key = JSON.stringify(
      rects.map((r) => [r.x, r.z, r.hw, r.hd, r.moveBonus]),
    );
    if (key === roadKey) return;
    roadKey = key;
    roads.setRoads(rects);
    nav.setRoads(rects);
  };

  // Pilum volley tracers (presentation only; damage stays in sim/combat).
  const volley = new VolleyField(world.scene, world.groundY);
  const cycle = new DayNightCycle();  const fog = new FogOfWarRenderer(world, vision);
  const torchField = new TorchField(world.scene);
  const brazierLights = new BrazierLights(world.scene);

  /** Fresh workforce context each tick (nodes deplete, farms complete). */
  const workforceCtx = () => ({
    nodes,
    farms: playerBuildings().filter(
      (b) => b.state === "active" && farmResource(b) !== null,
    ),
    forums: buildings.filter(
      (b) => b.state === "active" && b.def.id === playerCommandId,
    ),
    sites: buildings,
    roads,
    economy,
    fx: techFx,
  });

  // --- Buildings (both teams) ---
  const buildings: PlacedBuilding[] = [];
  const buildingViews: BuildingView[] = [];
  const playerTint = new THREE.Color(playerFaction.palette.primary).getHex();
  const enemyTint = new THREE.Color(enemyFaction.palette.primary).getHex();

  const addBuilding = (
    def: BuildingDef,
    x: number,
    z: number,
    instant: boolean,
    team: Team = 0,
    forceUid?: number,
  ): PlacedBuilding => {
    const b = placeBuilding(def, x, z, instant, team, forceUid);
    buildings.push(b);
    const view = createBuildingView(
      b,
      team === 0 ? playerTint : enemyTint,
      world.groundY,
      db.buildingModel(def.id, echelonState.index),
      modelStore,
    );
    buildingViews.push(view);
    world.selectables.add(view.group);
    syncBuildingTargets();
    if (!instant && b.state === "constructing") {
      // Fabers visibly build: the nearest idle workers walk to the site and
      // hammer until it completes (purely visual — construction stays
      // time-based). The AI's workforce is reachable via the closure.
      const wf = team === 0 ? workforce : ai.workforce;
      wf.assignBuilders(b, 2, team);
    }
    return b;
  };

  const removeBuilding = (b: PlacedBuilding): void => {
    const bi = buildings.indexOf(b);
    if (bi >= 0) buildings.splice(bi, 1);
    const vi = buildingViews.findIndex((v) => v.building === b);
    if (vi >= 0) {
      world.selectables.remove(buildingViews[vi].group);
      buildingViews.splice(vi, 1);
    }
    syncBuildingTargets();
  };

  // --- Building targets for attack orders ---
  // Declared here (before any addBuilding call) so early placements during
  // setup don't hit the temporal dead zone on `buildingTargets`.
  let buildingTargets: BuildingTarget[] = [];
  function syncBuildingTargets(): void {
    buildingTargets = buildings
      .filter((b) => b.state === "active")
      .map((b) => ({ kind: "building" as const, building: b, team: b.team }));
  }
  syncBuildingTargets();

  // --- Fighters (both teams) ---
  const fighters: Combatant[] = [];
  const playerBuildings = (): PlacedBuilding[] =>
    buildings.filter((b) => b.team === 0);
  const formationDef =
    db.formations.get("cohort_line") ?? db.formations.get("loose") ?? {
      id: "fallback",
      name_en: "Line",
      echelon: 1,
      implemented: true,
      shape: "line",
      spacing: 1.5,
      depth_ratio: 0.28,
      front_arc_degrees: 150,
      modifiers: { move_speed: 1 },
    };

  /**
   * Muster a formation at an organization tier. The tier sets the field
   * bodies (simulated/rendered soldiers); paperStrength (upkeep +
   * population) comes from the tier's books, except single-figure
   * retinues (commanders) which pay on actual heads.
   */
  const spawnFighter = (
    unit: UnitDef,
    team: Team,
    x: number,
    z: number,
    label: string,
    tier: OrgTier,
    fdef: typeof formationDef = formationDef,
  ): Combatant => {
    const role = unit.extra["role"] as string | undefined;
    const mods = team === 0
      ? {
        damageBonus: techFx.unitStatAdd(role, "damage"),
        armourBonus: techFx.unitStatAdd(role, "melee_armour"),
      }
      : {};
    const bodies =
      role === "commander" ? 1 : tier.fieldBodies;
    const view = createFormationView(
      fdef,
      label,
      bodies,
      { x, z },
      team === 0 ? Math.PI : 0,
      team === 0 ? playerTint : enemyTint,
      world.groundY,
      { unit, team, art: db.unitModel(unit.id), anim: animSys, store: modelStore },
    );
    const c = makeCombatant(view, unit, team, bodies, mods, {
      tierId: tier.id,
      paperStrength: resolvePaperStrength(unit, tier, bodies),
    });
    view.group.userData.fighter = c;
    // Animation inputs: defensive formations hold shields-up; the throw
    // clip's release event looses a pilum volley along the facing.
    view.animDefensive = DEFENSIVE_FORMATION_IDS.has(fdef.id);
    view.onRelease = (x, z, heading) => volley.spawn(x, z, heading);
    fighters.push(c);
    world.selectables.add(view.group);
    return c;
  };

  /**
   * Tiers the player may muster right now: the echelon's muster set
   * (sim/organization.ts MUSTER_SETS) filtered by researched tech gates,
   * with fallback to lower echelons. The player chooses a tier per
   * training order — unlike the AI, which always takes the best unlocked.
   */
  const playerMusterTiers = (): OrgTier[] =>
    musterTiers(playerFactionId, echelonState.index, research.completed);

  const removeFighter = (c: Combatant): void => {
    const i = fighters.indexOf(c);
    if (i >= 0) fighters.splice(i, 1);
    world.selectables.remove(c.view.group);
    if (selection.selected === c.view.group) selection.setSelected(null);
    selection.removeFromMany(c.view.group);
  };

  // Player starting buildings + starting units straight from faction data.
  // Spots are relative to the terrain-snapped player base site.
  const startSpots: Array<[number, number]> = [
    [0, 0], [-14, 6], [14, -6], [-6, -16], [6, 16],
  ].map(([sx, sz]) => [PLAYER_BASE.x + sx, PLAYER_BASE.z + sz] as [number, number]);
  let spot = 0;
  for (const [id, count] of Object.entries(playerFaction.starting_buildings ?? noStarts)) {
    const def = db.buildings.get(id);
    if (!def) {
      console.warn(`[buildings] starting building '${id}' not in data mirror`);
      continue;
    }
    for (let i = 0; i < count && spot < startSpots.length; i++, spot++) {
      addBuilding(def, startSpots[spot][0], startSpots[spot][1], true, 0);
    }
  }
  // Starting units from faction data: workers join the workforce,
  // commanders and military spawn as combatants near the base.
  let unitNum = 0;
  for (const [unitId, count] of Object.entries(playerFaction.starting_units ?? noStarts)) {
    const unit = db.units.get(unitId);
    if (!unit) continue;
    for (let i = 0; i < count; i++) {
      unitNum++;
      if (unit.extra["role"] === "worker") {
        const wp = snapToGround(
          PLAYER_BASE.x + 8 + unitNum * 2.2,
          PLAYER_BASE.z + 8 - unitNum * 1.4,
        );
        const w = workforce.spawn(unit, 0, wp.x, wp.z);
        workforce.autoAssign(w, workforceCtx());
      } else {
        const fp = snapToGround(
          PLAYER_BASE.x + 24 + unitNum * 3,
          PLAYER_BASE.z + 10 - unitNum,
        );
        const c = spawnFighter(
          unit,
          0,
          fp.x,
          fp.z,
          `${unit.name_en} ${unitNum}`,
          // Commanders muster as single figures; the tier is irrelevant.
          playerMusterTiers()[0] ?? {
            id: "", faction: playerFactionId, name_en: "",
            paperStrength: 1, fieldBodies: 1,
            techGate: null, minEchelon: 1, appointments: [],
          },
        );
        if (unit.extra["role"] === "commander") {
          c.alive = 1;
          setFormationCount(c.view, 1, world.groundY);
        }
      }
    }
  }

  // Opposing camp: command building + training hall + two guard warbands.
  // Opposing camp: faction data decides the buildings, the military unit
  // is the faction's Echelon I infantry (fallback: any implemented unit).
  // (CAMP is derived from the skirmish map size just after faction select.)
  const enemyDefs = [...db.buildings.values()].filter((b) => b.faction === enemyFaction.id);
  const campCenter =
    db.buildings.get(enemyCommandId) ??
    enemyDefs.find((b) => b.id.includes("forum")) ??
    enemyDefs[0];
  // The war hall must train actual soldiers: the command building itself
  // often has a `trains` list (workers/chiefs), so a naive first-with-trains
  // lookup doubles it and the AI could never train military. Pick the first
  // building whose trains list includes a military-role unit (data-driven,
  // faction-agnostic); fall back to the old behavior if none exists.
  const MILITARY_ROLES_BOOT = new Set([
    "infantry",
    "skirmisher",
    "cavalry",
    "archer",
    "siege",
  ]);
  const warHall =
    enemyDefs.find((b) =>
      ((b.extra["trains"] as string[]) ?? []).some((id) => {
        const u = db.units.get(id);
        return (
          u !== undefined && MILITARY_ROLES_BOOT.has(String(u.extra["role"]))
        );
      }),
    ) ??
    enemyDefs.find((b) => ((b.extra["trains"] as string[]) ?? []).length > 0);
  if (campCenter) addBuilding(campCenter, CAMP.x, CAMP.z, true, 1); // enemy command
  if (warHall) {
    const wh = snapToGround(CAMP.x + 16, CAMP.z + 6);
    addBuilding(warHall, wh.x, wh.z, true, 1);
  }
  const warrior =
    [...db.units.values()].find(
      (u) =>
        u.faction === enemyFaction.id &&
        u.extra["role"] === "infantry" &&
        u.echelon === 1 &&
        u.extra["implemented"] !== false,
    ) ??
    [...db.units.values()].find(
      (u) => u.faction === enemyFaction.id && u.extra["implemented"] !== false,
    );
  if (warrior) {
    // The AI opens at its baseline muster tier; drilling up happens
    // through its org-tech drill (see the org drill in AiDirector.tickResearch).
    const aiBaseline = bestTierForFaction(enemyFactionId, new Set());
    const wa = snapToGround(CAMP.x - 14, CAMP.z + 10);
    const wb = snapToGround(CAMP.x + 10, CAMP.z - 12);
    spawnFighter(warrior, 1, wa.x, wa.z, "Warband A", aiBaseline);
    spawnFighter(warrior, 1, wb.x, wb.z, "Warband B", aiBaseline);
  }
  /**
   * AI director callbacks: render-side construction (views, world lists)
   * lives here; all sim state (economy, workforce, research, queues,
   * knowledge) lives in the director. The closures see the current `ai`
   * binding, so they keep working after a load recreates the director.
   */
  let ai: AiDirector;
  const aiCallbacks: AiCallbacks = {
    spawnMilitary: (unit, x, z, label, tier) => {
      const p = snapToGround(x, z);
      return spawnFighter(unit, 1, p.x, p.z, label, tier);
    },
    spawnWorker: (unit, x, z) => {
      const p = snapToGround(x, z);
      return ai.workforce.spawn(unit, 1, p.x, p.z);
    },
    placeBuilding: (def, x, z, instant, team) =>
      addBuilding(def, x, z, instant, team),
    orderMove: (c, x, z) =>
      orderFormationMove(
        c.view,
        x,
        z,
        Math.atan2(x - c.view.anchor.x, z - c.view.anchor.z),
        { nav, team: c.team, simNow },
      ),
    notify: (msg) => hud.showSelection(msg),
  };
  ai = new AiDirector(db, enemyFaction, CAMP.x, CAMP.z, difficulty, aiCallbacks, field);
  ai.roads = roads;
  ai.bindResourceNodes(nodes);
  ai.onStart();

  /**
   * Faction-grid vision (20k-unit scale): units never cast viewsheds.
   * Each unit's vision cost is one O(1) stamp of its position into its
   * faction's observer grid (`vision.stampUnit`); `vision.update()` casts
   * a single raycast viewshed per occupied observer cell from the stamped
   * centroid. Buildings are few, so they keep individual data-driven
   * viewers (sorted for determinism: building jitter seeds are dense
   * indices, so stable order = stable shapes). Fighter torches fold into
   * the fighter cluster — at every night factor the fighter night range
   * (110→30) already exceeds the 26u torch bubble, so the union is the
   * fighter viewshed (see src/sim/vision.ts).
   */
  /** Finite number from UE data, or undefined when the field is absent. */
  const numOpt = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : undefined;

  /** O(1) per unit: stamp positions into the faction observer grids. */
  const stampUnits = (): void => {
    for (const f of fighters) {
      if (f.alive <= 0) continue;
      vision.stampUnit(f.team, "fighter", f.view.anchor.x, f.view.anchor.z);
    }
    for (const w of workforce.workers) {
      vision.stampUnit(w.team, "worker", w.x, w.z);
    }
    for (const w of ai.workforce.workers) {
      vision.stampUnit(w.team, "worker", w.x, w.z);
    }
  };

  /** Individual data-driven building/brazier viewers (few — not stamped). */
  const buildingViewers = (): VisionViewer[] => {
    const viewers: VisionViewer[] = [];
    for (const b of buildings) {
      if (b.state !== "active") continue;
      // UE data `vision` / `vision_height` fields drive the vision
      // system directly (buildings_*.json); the kind tables in vision.ts
      // are fallbacks only.
      const dataRange = numOpt(b.def.extra["vision"]);
      const dataEye = numOpt(b.def.extra["vision_height"]);
      const [fw, fd] = footprintOf(b.def);
      viewers.push({
        x: b.x,
        z: b.z,
        team: b.team,
        kind: b.def.id === BRAZIER_ID ? "brazier" : "building",
        ...(dataRange !== undefined ? { rangeDay: dataRange } : {}),
        ...(dataEye !== undefined ? { eyeHeight: dataEye } : {}),
        // The viewer's own footprint never occludes its own sight.
        clearRadius: Math.hypot(fw, fd) / 2 + 0.5,
      });
    }
    viewers.sort(
      (a, b) =>
        a.team - b.team || a.x - b.x || a.z - b.z || (a.kind < b.kind ? -1 : 1),
    );
    return viewers;
  };

  // --- Selection ---
  const selection = new Selection(
    world.camera,
    world.renderer.domElement,
    world.selectables,
    (obj) => {
      for (const f of fighters) {
        (f.view.mesh.material as THREE.MeshStandardMaterial).emissive.setHex(
          obj === f.view.group ? 0x553311 : 0x000000,
        );
      }
      for (const v of buildingViews) highlightBuilding(v, obj === v.group);
    },
  );

  const findBuilding = (obj: THREE.Object3D | null): PlacedBuilding | null => {
    const uid = obj?.userData.buildingUid as number | undefined;
    return buildings.find((b) => b.uid === uid) ?? null;
  };

  /** The individually selected worker, if the worker mesh is selected. */
  const selectedWorker = (): Worker | null => {
    if (selection.selected !== workerField.mesh) return null;
    const id = selection.lastInstanceId;
    if (id == null) return null;
    const w = workerField.picked[id] ?? null;
    // Fog of war: a hidden enemy worker can't be inspected.
    if (w && w.team === 1 && !vision.isVisible(0, w.x, w.z)) return null;
    return w;
  };

  /** Primary selection for single-target panels: the single pick, else the first box-selected. */
  const primarySelected = (): THREE.Object3D | null =>
    selection.selected ?? selection.selectedMany[0] ?? null;

  const selectionLabel = (): string => {
    if (selection.selectedMany.length > 1) {
      const names = selection.selectedMany
        .map((o) => (o.userData.fighter as Combatant | undefined)?.unit.name_en)
        .filter((n): n is string => !!n);
      const distinct = [...new Set(names)];
      return `<b>${selection.selectedMany.length} fighters</b> — ${distinct.join(", ")}. Tap ground to order the group.`;
    }
    const obj = primarySelected();
    if (!obj) return "nothing";
    const w = selectedWorker();
    if (w) {
      const cap = workforce.carryCap(w, techFx);
      const task =
        w.state === "harvesting"
          ? `gathering ${w.node ? w.node.kind : "food"}`
          : w.state === "toDropoff"
            ? "hauling to forum"
            : w.state === "toNode"
              ? "moving to source"
              : w.state;
      return `<b>${w.unit.name_en}</b> — ${task} (${w.carried.toFixed(0)}/${cap})`;
    }
    const fighter = obj.userData.fighter as Combatant | undefined;
    if (fighter) {
      const who = fighter.team === 0 ? fighter.unit.name_en : `Enemy ${fighter.unit.name_en}`;
      const tier = tierById(fighter.tierId);
      const tierLabel = tier ? `${tier.name_en} · ` : "";
      const upkeep = formationUpkeepPerMin(fighter.paperStrength).toFixed(1);
      const arrears = fighter.unpaid
        ? ` · <span style="color:#d66;">UNPAID (${fighter.supplyDebt.toFixed(0)} food owed)</span>`
        : "";
      const status = fighter.routing
        ? "ROUTING"
        : `${fighter.alive} men · morale ${Math.round(fighter.morale)} · ${upkeep} food/min${arrears}`;
      return `<b>${who}</b> — ${tierLabel}${status}`;
    }
    const b = findBuilding(obj);
    if (b) {
      const state = b.state === "active"
        ? `operational · ${Math.round(b.hp)}/${b.maxHp} hp`
        : `under construction ${Math.round(b.progress * 100)}%`;
      const trains = (b.def.extra["trains"] as string[] | undefined) ?? [];
      const trainsLabel = trains.length > 0 ? ` — trains: ${trains.length} unit types` : "";
      const enemy = b.team === 1 ? " (enemy)" : "";
      return `<b>${b.def.name_en}${enemy}</b> — ${state}${trainsLabel}`;
    }
    return obj.userData.label ?? obj.name ?? "nothing";
  };
  /** Unit medallion spec for the current selection (fighters + workers). */
  const selectionMedallionSpec = (): MedallionSpec | null => {
    const obj = primarySelected();
    if (!obj) return null;
    const w = selectedWorker();
    if (w) {
      return {
        artKey: artKeyFor(db.unitModel(w.unit.id), w.unit.id),
        name: w.unit.name_en,
        tint: playerTint,
        model: db.unitModel(w.unit.id),
      };
    }
    const fighter = obj.userData.fighter as Combatant | undefined;
    if (fighter) {
      return {
        artKey: artKeyFor(db.unitModel(fighter.unit.id), fighter.unit.id),
        name: fighter.unit.name_en,
        tint: fighter.team === 0 ? playerTint : enemyTint,
        model: db.unitModel(fighter.unit.id),
      };
    }
    return null;
  };

  /** Medallion canvas for the selection bar (intro plays once per type). */
  const selectionMedallionCanvas = (): HTMLCanvasElement | null => {
    const spec = selectionMedallionSpec();
    if (!spec) return null;
    const c = document.createElement("canvas");
    c.width = MEDALLION_PX;
    c.height = MEDALLION_PX;
    c.style.cssText =
      "width:56px;height:56px;border-radius:50%;flex:0 0 auto;" +
      "background:radial-gradient(circle at 50% 38%, #2a2d33, #141518);" +
      "border:1px solid #b98b3a;";
    medallions.attachWithIntro(c, spec);
    return c;
  };

  selection.onSelect = () => {
    hud.showSelection(`Selected: ${selectionLabel()}`);
    hud.setMedallion(selectionMedallionCanvas());
    refreshTrainPanel();
  };

  // --- Build menu + placement (player, unchanged from Phase 2) ---
  const computeBuildables = (): BuildingDef[] => [
    ...[...db.buildings.values()]
      .filter(
        (d) =>
          d.faction === playerFaction.id &&
          (d.extra["echelon"] as number) <= echelonState.index &&
          d.extra["implemented"] === true,
      )
      .sort((a, b) => a.name_en.localeCompare(b.name_en)),
  ];
  const buildMenu = new BuildMenu(computeBuildables());
  let blueprint: BuildingDef | null = null;
  buildMenu.onBlueprint = (def) => {
    blueprint = def;
    selection.suspended = def !== null;
    if (def) hud.showSelection(`Placing <b>${def.name_en}</b> — click open ground, Esc to cancel.`);
  };

  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const setNdc = (clientX: number, clientY: number): void => {
    const rect = world.renderer.domElement.getBoundingClientRect();
    ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
  };
  /** Walk a raycast hit up to the selectable root under selectables. */
  const pickSelectable = (clientX: number, clientY: number): THREE.Object3D | null => {
    setNdc(clientX, clientY);
    raycaster.setFromCamera(ndc, world.camera);
    const hits = raycaster.intersectObjects(world.selectables.children, true);
    // Fog of war: hidden enemies can't be picked — take the first hit that
    // passes visibility (an explored enemy building is pickable as a ghost).
    for (const hit of hits) {
      let obj: THREE.Object3D | null = hit.object;
      while (obj && obj.parent !== world.selectables && obj.parent) obj = obj.parent;
      if (!obj) continue;
      const f = obj.userData.fighter as Combatant | undefined;
      if (
        f &&
        f.team === 1 &&
        !vision.isVisible(0, f.view.anchor.x, f.view.anchor.z)
      ) {
        continue;
      }
      const b = findBuilding(obj);
      if (b && b.team === 1 && !vision.exploredAt(0, b.x, b.z)) continue;
      return obj;
    }
    return null;
  };

  /**
   * Attempt blueprint placement at the given client coordinates.
   * Returns true when a placement was attempted (hit or miss), so tap
   * routing knows the gesture was consumed.
   */
  const tryPlaceBlueprint = (clientX: number, clientY: number): boolean => {
    if (!blueprint) return false;
    setNdc(clientX, clientY);
    raycaster.setFromCamera(ndc, world.camera);
    const hit = raycaster.intersectObject(world.terrain, false)[0];
    if (!hit) return true;
    const { x, z } = hit.point;
    const cost = costOf(blueprint);
    const missing = missingRequirements(
      blueprint,
      playerBuildings(),
      research.completed,
    );
    if (Math.abs(x) > mapSizeDef.halfExtent || Math.abs(z) > mapSizeDef.halfExtent) {
      hud.showSelection("Too close to the map edge.");
    } else if (missing.length > 0) {
      hud.showSelection(`Requires: ${missing.join(", ")}.`);
    } else if (overlaps(blueprint, x, z, buildings)) {
      hud.showSelection("No room there — pick clear ground.");
    } else if (!economy.pay(cost)) {
      hud.showSelection("Insufficient resources.");
    } else {
      addBuilding(blueprint, x, z, false, 0);
      hud.showSelection(`<b>${blueprint.name_en}</b> under construction.`);
    }
    buildMenu.refresh(economy, buildings, research.completed);
    return true;
  };

  window.addEventListener(
    "mousedown",
    (e) => {
      if (!blueprint || e.button !== 0) return;
      if (e.target !== world.renderer.domElement) return;
      tryPlaceBlueprint(e.clientX, e.clientY);
      e.stopPropagation();
      e.preventDefault();
    },
    true,
  );

  // --- Training ---
  const queues = new TrainingQueues();
  const trainPanel = new TrainPanel();
  // Training buttons show the same cached unit medallions (static).
  trainPanel.medallions = {
    renderer: medallions,
    tint: playerTint,
    artKeyFor: (u) => artKeyFor(db.unitModel(u.id), u.id),
    modelFor: (u) => db.unitModel(u.id),
  };
  const selectedTrainingBuilding = (): PlacedBuilding | null => {
    const b = findBuilding(selection.selected);
    if (!b || b.team !== 0 || b.state !== "active") return null;
    return trainableUnits(b, db, echelonState.index, research).length > 0 ? b : null;
  };
  function refreshTrainPanel(): void {
    const b = selectedTrainingBuilding();
    const status = commandStatus(
      fighters,
      0,
      queues,
      db,
      playerBuildings().filter((x) => x.state === "active"),
      techFx,
    );
    trainPanel.render(
      b,
      b ? trainableUnits(b, db, echelonState.index, research) : [],
      economy,
      queues,
      status,
      // Muster options: one button per tier the player may muster.
      // Workers get no options and train as ordinary single units.
      (u) => {
        if (!consumesCommand(u)) return [];
        return playerMusterTiers().map((tier) => ({
          tierId: tier.id,
          tierName: tier.name_en,
          paperStrength: tier.paperStrength,
          cost: tierTrainCost(scaledTrainCost(u), tier),
          buildTimeSec:
            (((u.extra["build_time"] as number) ?? 20) * trainScale(tier)),
          upkeepFoodPerMin: formationUpkeepPerMin(tier.paperStrength),
        }));
      },
    );
  }
  /** Training cost with tech multipliers applied. */
  const scaledTrainCost = (unit: UnitDef): Record<string, number> => {
    const mult = techFx.trainCostMultiplier(unit.extra["role"] as string | undefined);
    const out: Record<string, number> = {};
    for (const [id, v] of Object.entries(unitCost(unit))) {
      out[id] = Math.ceil(v * mult);
    }
    return out;
  };
  trainPanel.onTrain = (unit, tierId) => {
    const b = selectedTrainingBuilding();
    if (!b) return;
    // Command gate: 1 command-consuming formation = 1 command. Workers cost 0.
    if (consumesCommand(unit)) {
      const status = commandStatus(
        fighters,
        0,
        queues,
        db,
        playerBuildings().filter((x) => x.state === "active"),
        techFx,
      );
      const gate = canTrain(unit, status);
      if (!gate.ok) {
        hud.showSelection(
          `<b>${gate.reason}.</b> Build more military buildings or research <b>Dilectus</b>.`,
        );
        return;
      }
    }
    // The muster tier is chosen on the button and locked at enqueue: cost
    // and time scale with the formation's paper strength (what you pay for
    // is what musters). Workers muster as ordinary single units.
    const tier = tierById(tierId) ?? null;
    const cost = tier ? tierTrainCost(scaledTrainCost(unit), tier) : scaledTrainCost(unit);
    if (!economy.pay(cost)) {
      hud.showSelection("Insufficient resources.");
      return;
    }
    queues.enqueue(b.uid, unit, tier?.id ?? "", tier ? trainScale(tier) : 1);
    hud.showSelection(
      tier
        ? `Training <b>${tier.name_en}</b> (${unit.name_en}) at ${b.def.name_en} — ` +
          `${tier.paperStrength} paper strength.`
        : `Training <b>${unit.name_en}</b> at ${b.def.name_en}.`,
    );
    refreshTrainPanel();
  };

  // --- Research + echelons ---
  const researchPanel = new ResearchPanel();
  researchPanel.bind({
    db,
    economy,
    research,
    echelon: echelonState,
    activeBuildingIds: () =>
      new Set(
        playerBuildings().filter((b) => b.state === "active").map((b) => b.def.id),
      ),
    activeMilitaryIds: () =>
      new Set(
        playerBuildings()
          .filter(
            (b) =>
              b.state === "active" &&
              ((b.def.extra["trains"] as string[]) ?? []).length > 0,
          )
          .map((b) => b.def.id),
      ),
    advanceStats: () => ({
      // Population = represented heads: paper strength on the books for
      // formations, plus civilian workers. Distinct from command
      // (formation count) and upkeep (logistics cost).
      population:
        fighters
          .filter((f) => f.team === 0)
          .reduce((n, f) => n + f.paperStrength, 0) +
        workforce.workers.filter((w) => w.team === 0).length,
      production: economy.rateRecord(),
      strategicPoints,
    }),
    onEchelonAdvanced: (name) => {
      hud.showSelection(`<b>${name}</b> — new buildings and units unlocked.`);
      buildMenu.setDefs(computeBuildables());
      refreshTrainPanel();
      // Echelon-band visual upgrade: swap building models whose band changed.
      refreshBuildingArt(
        buildingViews,
        (id, e) => db.buildingModel(id, e),
        echelonState.index,
        world.groundY,
        modelStore,
      );
    },
    onTechResearched: () => {
      refreshTrainPanel();
    },
  });

  // --- Save / load (localStorage) ---
  const SAVE_KEY = `legionnaire-save-v1-${playerFactionId}`;
  const LEGACY_SAVE_KEY = "legionnaire-save-v1";
  interface SaveData {
    version: 1;
    faction: string;
    difficulty: DifficultyId;
    /** Skirmish map size; absent in pre-map-size saves (treated as standard). */
    mapSize?: MapSizeId;
    simNow: number;
    stocks: Record<string, number>;
    research: {
      completed: string[];
      active: { techId: string; remaining: number; total: number } | null;
    };
    ai: {
      stocks: Record<string, number>;
      research: {
        completed: string[];
        active: { techId: string; remaining: number; total: number } | null;
      };
    };
    echelonIndex: number;
    strategicPoints: number;
    timeOfDay: number;
    explored: string;
    buildings: Array<{
      uid: number; defId: string; x: number; z: number; team: 0 | 1;
      state: string; progress: number; hp: number;
    }>;
    fighters: Array<{
      unitId: string; team: 0 | 1; x: number; z: number;
      alive: number; morale: number;
      tierId?: string; paperStrength?: number; supplyDebt?: number;
      desertFrac?: number; unpaid?: boolean; deserted?: boolean;
    }>;
    workers: Array<{
      id: number; unitId: string; team: 0 | 1; x: number; z: number;
      state: WorkerState; nodeId: number | null; farmUid: number | null;
      siteUid: number | null;
      carriedKind: string | null; carried: number;
    }>;
    nodes: Array<{
      id: number; kind: NodeKind; x: number; z: number;
      amount: number; maxAmount: number;
    }>;
    queues: Array<{
      buildingUid: number; unitId: string; tierId: string;
      remaining: number; total: number;
    }>;
  }

  function collectSave(): SaveData {
    return {
      version: 1,
      faction: playerFactionId,
      difficulty,
      mapSize: mapSizeId,
      simNow,
      stocks: economy.stockRecord(),
      research: {
        completed: [...research.completed],
        active: research.active ? { ...research.active } : null,
      },
      ai: {
        stocks: ai.economy.stockRecord(),
        research: {
          completed: [...ai.research.completed],
          active: ai.research.active ? { ...ai.research.active } : null,
        },
      },
      echelonIndex: echelonState.index,
      strategicPoints,
      timeOfDay: cycle.serialize(),
      explored: vision.serialize(),
      buildings: buildings.map((b) => ({
        uid: b.uid, defId: b.def.id, x: b.x, z: b.z, team: b.team,
        state: b.state, progress: b.progress, hp: b.hp,
      })),
      fighters: fighters.map((f) => ({
        unitId: f.unit.id, team: f.team,
        x: f.view.anchor.x, z: f.view.anchor.z,
        alive: f.alive, morale: f.morale,
        tierId: f.tierId, paperStrength: f.paperStrength,
        supplyDebt: f.supplyDebt,
        desertFrac: f.desertFrac, unpaid: f.unpaid, deserted: f.deserted,
      })),
      workers: [...workforce.workers, ...ai.workforce.workers].map((w) => ({
        id: w.id, unitId: w.unit.id, team: w.team, x: w.x, z: w.z,
        state: w.state, nodeId: w.node?.id ?? null,
        farmUid: w.farm?.uid ?? null,
        siteUid: w.siteUid,
        carriedKind: w.carriedKind, carried: w.carried,
      })),
      nodes: nodes.map((n) => ({ ...n })),
      // Both teams' training queues; team-1 orders restore into the AI.
      queues: [...queues.allOrders(), ...ai.queues.allOrders()],
    };
  }

  function applySave(data: SaveData): void {
    if (data.version !== 1) throw new Error("unsupported save version");
    if (data.faction && data.faction !== playerFactionId) {
      throw new Error(
        `save was made as ${data.faction} — restart as that faction to load it`,
      );
    }
    banner.hide();
    selection.setSelected(null);
    // Clear live state.
    for (const f of [...fighters]) removeFighter(f);
    for (const b of [...buildings]) removeBuilding(b);
    for (const w of [...workforce.workers]) workforce.remove(w);
    nodes.length = 0;
    queues.clear();
    research.completed.clear();
    research.active = null;
    // Core state.
    simNow = data.simNow;
    if (
      data.difficulty === "easy" ||
      data.difficulty === "advanced" ||
      data.difficulty === "expert"
    ) {
      difficulty = data.difficulty;
    }
    // Map size: the terrain/nav/vision grids are fixed at boot, so a save
    // from a different size restores its entities at absolute positions and
    // re-targets the camera clamp + minimap. Same-size loads are exact.
    const saveMapSize: MapSizeId = isMapSizeId(data.mapSize) ? data.mapSize : "standard";
    if (saveMapSize !== mapSizeId) {
      console.warn(
        `[save] map size mismatch: save is ${saveMapSize}, world booted as ${mapSizeId}; ` +
          "entities restore at absolute positions, camera/minimap re-targeted.",
      );
    }
    const saveDef = MAP_SIZES[saveMapSize];
    camera.setBounds(saveDef.halfExtent);
    minimap.setWorldSize(saveDef.worldSize);
    economy.setStocks(data.stocks);
    for (const id of data.research.completed) {
      if (db.technologies.has(id)) research.completed.add(id);
    }
    if (data.research.active && db.technologies.has(data.research.active.techId)) {
      research.active = { ...data.research.active };
    }
    echelonState.index = Math.min(Math.max(data.echelonIndex, 1), 5);
    strategicPoints = data.strategicPoints;
    cycle.restore(data.timeOfDay ?? 0.32);
    if (data.explored) {
      try {
        vision.restore(data.explored);
      } catch {
        vision.reset();
      }
    } else {
      vision.reset();
    }
    // Nodes.
    for (const n of data.nodes) {
      nodes.push({ ...n });
      reserveNodeId(n.id);
    }
    nodeField.sync(nodes);
    // Fresh AI director on the restored clock (knowledge resets: it
    // re-scouts). Its stocks/research come from the save when present.
    ai = new AiDirector(db, enemyFaction, CAMP.x, CAMP.z, difficulty, aiCallbacks, field);
    ai.roads = roads;
    ai.bindResourceNodes(nodes);
    if (data.ai) {
      ai.economy.setStocks(data.ai.stocks);
      for (const id of data.ai.research.completed) {
        if (db.technologies.has(id)) ai.research.completed.add(id);
      }
      if (
        data.ai.research.active &&
        db.technologies.has(data.ai.research.active.techId)
      ) {
        ai.research.active = { ...data.ai.research.active };
      }
    }
    // Buildings (uids preserved so queues/farms relink).
    for (const s of data.buildings) {
      // Prototype-era saves used the invented id "brazier"; remap it to
      // the canonical signal_brazier from the data mirror.
      const def = db.buildings.get(
        s.defId === "brazier" ? BRAZIER_ID : s.defId,
      );
      if (!def) continue;
      const b = addBuilding(def, s.x, s.z, s.state === "active", s.team, s.uid);
      b.progress = s.progress;
      b.hp = Math.min(s.hp, b.maxHp);
      if (s.state !== "active") b.state = "constructing";
      const v = buildingViews.find((bv) => bv.building === b);
      if (v) updateBuildingView(v);
    }
    syncBuildingTargets();
    // Fighters.
    let n = 0;
    for (const s of data.fighters) {
      const unit = db.units.get(s.unitId);
      if (!unit) continue;
      // Tier/paper restore with graceful fallback for pre-org saves:
      // paper falls back to the saved alive count.
      const tier = tierById(s.tierId ?? "") ?? null;
      const paper = s.paperStrength ?? s.alive ?? 1;
      const c = spawnFighter(
        unit,
        s.team,
        s.x,
        s.z,
        `${unit.name_en} ${++n}`,
        tier ?? {
          id: "", faction: "", name_en: "Warband",
          paperStrength: paper, fieldBodies: s.alive ?? 1,
          techGate: null, minEchelon: 1, appointments: [],
        },
      );
      c.paperStrength = paper;
      c.alive = Math.max(1, Math.min(s.alive, c.view.positions.length));
      c.morale = s.morale;
      c.supplyDebt = s.supplyDebt ?? 0;
      c.desertFrac = s.desertFrac ?? 0;
      c.unpaid = s.unpaid ?? false;
      c.deserted = s.deserted ?? false;
      setFormationCount(c.view, c.alive, world.groundY);
    }
    // Workers (team 1 joins the AI's workforce, not the player's).
    for (const s of data.workers) {
      const unit = db.units.get(s.unitId);
      if (!unit) continue;
      const wf = s.team === 1 ? ai.workforce : workforce;
      const w = wf.spawn(unit, s.team, s.x, s.z);
      w.id = s.id;
      reserveWorkerId(s.id);
      w.state = s.state;
      w.carried = s.carried;
      w.carriedKind = (s.carriedKind as Worker["carriedKind"]) ?? null;
      w.node = nodes.find((nd) => nd.id === s.nodeId) ?? null;
      w.farm = buildings.find((b) => b.uid === s.farmUid) ?? null;
      // Construction/repair assignments: re-link the site when it still
      // exists and still needs the work, else fall back to idle.
      const site = buildings.find((b) => b.uid === s.siteUid) ?? null;
      if (
        site &&
        ((s.state === "constructing" && site.state === "constructing") ||
          (s.state === "repairing" &&
            site.state === "active" &&
            site.hp < site.maxHp))
      ) {
        w.siteUid = site.uid;
        w.tx = site.x;
        w.tz = site.z;
      } else {
        if (s.state === "constructing" || s.state === "repairing") {
          w.state = "idle";
        }
        w.siteUid = null;
      }
      w.harvestT = 0;
      // Restore travel targets to the resumed position so hauling/gathering
      // can continue without stale coordinates.
      if (w.siteUid == null) {
        w.tx = w.x;
        w.tz = w.z;
      }
    }
    // Training queues (team 1 orders belong to the AI's queues).
    for (const q of data.queues) {
      const b = buildings.find((x) => x.uid === q.buildingUid);
      if (!b) continue;
      (b.team === 1 ? ai.queues : queues).restoreOrder(
        q.buildingUid,
        q.unitId,
        q.remaining,
        q.total,
        q.tierId ?? "",
      );
    }
    // Construction sites restored mid-build get their faber crews back
    // (workers whose assignment survived the save were re-linked above).
    for (const b of buildings) {
      if (b.state !== "constructing") continue;
      const crew = [...workforce.workers, ...ai.workforce.workers].filter(
        (w) => w.siteUid === b.uid,
      ).length;
      if (crew === 0) {
        (b.team === 1 ? ai.workforce : workforce).assignBuilders(b, 2, b.team);
      }
    }
    // Refresh UI.
    buildMenu.setDefs(computeBuildables());
    refreshTrainPanel();
    researchPanel.render();
    hud.refresh();
  }

  const saveBtn = document.createElement("button");
  saveBtn.textContent = "Save";
  saveBtn.style.cssText =
    "position:fixed;right:12px;top:12px;z-index:30;padding:6px 12px;" +
    "background:rgba(10,10,12,0.85);color:#e8e2d4;border:1px solid #3a2f22;" +
    "border-radius:6px;cursor:pointer;font-size:12px;";
  saveBtn.addEventListener("click", () => {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(collectSave()));
      hud.showSelection("Game saved.");
    } catch {
      hud.showSelection("Could not save (storage unavailable).");
    }
  });
  const loadBtn = document.createElement("button");
  loadBtn.textContent = "Load";
  loadBtn.style.cssText = saveBtn.style.cssText.replace("right:12px", "right:76px");
  loadBtn.addEventListener("click", () => {
    const raw =
      localStorage.getItem(SAVE_KEY) ?? localStorage.getItem(LEGACY_SAVE_KEY);
    if (!raw) {
      hud.showSelection("No save found.");
      return;
    }
    try {
      applySave(JSON.parse(raw) as SaveData);
      hud.showSelection("Game loaded.");
    } catch (e) {
      hud.showSelection(
        e instanceof Error ? e.message : "Save is corrupted — starting position kept.",
      );
    }
  });
  document.body.append(saveBtn, loadBtn);

  // --- Orders: right-click = attack enemy under cursor, else move ---
  // (On touch, the same logic runs through handleTap's smart-tap routing.)
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const hitPoint = new THREE.Vector3();

  /** Worker order at a screen point: gather node/farm under cursor, else move. */
  const issueWorkerOrder = (clientX: number, clientY: number, w: Worker): void => {
    setNdc(clientX, clientY);
    raycaster.setFromCamera(ndc, world.camera);
    if (raycaster.ray.intersectPlane(groundPlane, hitPoint)) {
      const node = nodes.find(
        (n) => Math.hypot(n.x - hitPoint.x, n.z - hitPoint.z) < 7,
      );
      if (node) {
        workforce.assignNode(w, node);
        hud.showSelection(`<b>${w.unit.name_en}</b> — harvesting ${node.kind}.`);
        return;
      }
      const farm = playerBuildings().find(
        (b) =>
          b.state === "active" &&
          b.hp >= b.maxHp &&
          farmResource(b) !== null &&
          Math.hypot(b.x - hitPoint.x, b.z - hitPoint.z) < 9,
      );
      if (farm) {
        workforce.assignFarm(w, farm);
        hud.showSelection(`<b>${w.unit.name_en}</b> — working the farm.`);
        return;
      }
      // Repair: right-click one of your damaged buildings sends the worker
      // (plus two nearby idle hands) to fix it — they play the faber
      // repair clip while timber pays for the work. Checked before the
      // farm branch so a damaged farm gets repaired, not staffed.
      const damaged = playerBuildings().find((b) => {
        if (b.team !== 0 || b.state !== "active" || b.hp >= b.maxHp) {
          return false;
        }
        const [bw, bd] = footprintOf(b.def);
        return (
          Math.hypot(b.x - hitPoint.x, b.z - hitPoint.z) <
          Math.max(bw, bd) / 2 + 5
        );
      });
      if (damaged) {
        workforce.assignRepair(w, damaged);
        const n = workforce.assignRepairers(damaged, 2, 0);
        hud.showSelection(
          `<b>${w.unit.name_en}</b> — repairing ${damaged.def.name_en}` +
            (n > 0 ? ` (+${n} hands).` : "."),
        );
        return;
      }
      workforce.orderMove(w, hitPoint.x, hitPoint.z);
      hud.showSelection(`<b>${w.unit.name_en}</b> — moving.`);
    }
  };

  /**
   * Fighter order at a screen point: attack the enemy picked under the
   * cursor, else move the formation there. Returns true when an order or
   * attack was issued.
   */
  const issueFighterOrder = (
    clientX: number,
    clientY: number,
    fighter: Combatant,
    /** Group-move spread: world-unit offset added to ground destinations. */
    ox = 0,
    oz = 0,
  ): boolean => {
    const picked = pickSelectable(clientX, clientY);
    const pickedFighter = picked?.userData.fighter as Combatant | undefined;
    if (pickedFighter && pickedFighter.team === 1 && pickedFighter.alive > 0) {
      fighter.order = { target: pickedFighter };
      hud.showSelection(`<b>${fighter.unit.name_en}</b> — attacking ${pickedFighter.unit.name_en}!`);
      return true;
    }
    const pickedBuilding = findBuilding(picked);
    if (pickedBuilding && pickedBuilding.team === 1) {
      const target = buildingTargets.find((t) => t.building === pickedBuilding);
      if (target) {
        fighter.order = { target };
        hud.showSelection(`<b>${fighter.unit.name_en}</b> — attacking ${pickedBuilding.def.name_en}!`);
        return true;
      }
    }
    setNdc(clientX, clientY);
    raycaster.setFromCamera(ndc, world.camera);
    if (raycaster.ray.intersectPlane(groundPlane, hitPoint)) {
      const tx = hitPoint.x + ox;
      const tz = hitPoint.z + oz;
      const heading = Math.atan2(
        tx - fighter.view.anchor.x,
        tz - fighter.view.anchor.z,
      );
      fighter.order = null;
      orderFormationMove(fighter.view, tx, tz, heading, {
        nav,
        team: 0,
        simNow,
      });
      hud.showSelection(
        `<b>${fighter.unit.name_en}</b> — moving to (${tx.toFixed(0)}, ${tz.toFixed(0)}).`,
      );
      return true;
    }
    return false;
  };

  /**
   * Spread group move destinations on a loose grid so box-ordered
   * formations don't stack on one point. Attack orders ignore offsets.
   */
  const groupMoveOffsets = (n: number): Array<[number, number]> => {
    const offs: Array<[number, number]> = [];
    const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
    const rows = Math.max(1, Math.ceil(n / cols));
    for (let i = 0; i < n; i++) {
      offs.push([
        ((i % cols) - (cols - 1) / 2) * 14,
        (Math.floor(i / cols) - (rows - 1) / 2) * 14,
      ]);
    }
    return offs;
  };

  /** Issue an order to a fighter group, spreading ground destinations. */
  const issueGroupOrder = (clientX: number, clientY: number): boolean => {
    const group = orderedFighters();
    const offs = groupMoveOffsets(group.length);
    let ordered = false;
    group.forEach((fighter, i) => {
      ordered =
        issueFighterOrder(clientX, clientY, fighter, offs[i][0], offs[i][1]) ||
        ordered;
    });
    return ordered;
  };

  /**
   * Fighters that receive move/attack orders: the box-selected group when
   * one is active, else the singly selected fighter. Group orders fan out
   * to every live team-0 fighter in the set.
   */
  const orderedFighters = (): Combatant[] => {
    const many = selection.selectedMany
      .map((o) => o.userData.fighter as Combatant | undefined)
      .filter(
        (f): f is Combatant => !!f && f.team === 0 && f.alive > 0 && !f.routing,
      );
    if (many.length > 0) return many;
    const sel = selection.selected?.userData.fighter as Combatant | undefined;
    return sel && sel.team === 0 && sel.alive > 0 && !sel.routing ? [sel] : [];
  };

  world.renderer.domElement.addEventListener("mousedown", (e) => {
    if (e.button !== 2) return;
    // Worker orders: right-click a node/farm to gather, ground to move.
    const w = selectedWorker();
    if (w && w.team === 0) {
      issueWorkerOrder(e.clientX, e.clientY, w);
      return;
    }
    issueGroupOrder(e.clientX, e.clientY);
  });

  /**
   * Smart-tap routing for touch: one finger plays both mouse buttons.
   * - Tapping your own unit/building always selects it.
   * - Tapping an enemy or open ground with a fighter/worker selected issues
   *   the same order a right-click would.
   * - Otherwise the tap selects whatever is under it (or deselects).
   */
  const handleTap = (clientX: number, clientY: number): void => {
    if (tryPlaceBlueprint(clientX, clientY)) return;
    const picked = pickSelectable(clientX, clientY);
    const pickedFighter = picked?.userData.fighter as Combatant | undefined;
    const pickedBuilding = findBuilding(picked);
    const ownPicked =
      (pickedFighter !== undefined && pickedFighter.team === 0) ||
      (pickedBuilding !== null && pickedBuilding.team === 0) ||
      // The worker mesh is instanced (both teams); taps on it always select,
      // matching desktop left-click — orders come from the next tap.
      picked === workerField.mesh;
    if (!ownPicked) {
      const w = selectedWorker();
      if (w && w.team === 0) {
        issueWorkerOrder(clientX, clientY, w);
        return;
      }
      // Tap orders go to the whole box-selected group when one is active.
      if (issueGroupOrder(clientX, clientY)) return;
    }
    selection.selectAt(clientX, clientY);
  };

  // --- Box selection (touch select mode + desktop mouse drag) ---
  // One-finger drags pan the camera by default; the SELECT toggle below
  // switches drags to a rubber-band box that selects every own fighter
  // inside it. Group orders fan out via issueGroupOrder.
  const rubberBand = document.createElement("div");
  rubberBand.id = "select-rubber-band";
  rubberBand.style.cssText =
    "position:fixed;z-index:45;pointer-events:none;display:none;" +
    "border:1px solid #e8c67e;background:rgba(185,139,58,.14);";
  document.body.appendChild(rubberBand);
  const paintRubberBand = (r: SelectRect | null): void => {
    if (!r) {
      rubberBand.style.display = "none";
      return;
    }
    rubberBand.style.display = "";
    rubberBand.style.left = `${Math.min(r.x0, r.x1)}px`;
    rubberBand.style.top = `${Math.min(r.y0, r.y1)}px`;
    rubberBand.style.width = `${Math.abs(r.x1 - r.x0)}px`;
    rubberBand.style.height = `${Math.abs(r.y1 - r.y0)}px`;
  };
  /** Project a world point to CSS client pixels; null when behind camera. */
  const worldToClient = (x: number, z: number): { x: number; y: number } | null => {
    const rect = world.renderer.domElement.getBoundingClientRect();
    const v = new THREE.Vector3(x, world.groundY(x, z) + 1, z).project(
      world.camera,
    );
    if (v.z > 1) return null;
    return {
      x: rect.left + ((v.x + 1) / 2) * rect.width,
      y: rect.top + ((1 - v.y) / 2) * rect.height,
    };
  };
  const runBoxSelect = (r: SelectRect): void => {
    const x0 = Math.min(r.x0, r.x1);
    const x1 = Math.max(r.x0, r.x1);
    const y0 = Math.min(r.y0, r.y1);
    const y1 = Math.max(r.y0, r.y1);
    const inside = fighters.filter((f) => {
      if (f.team !== 0 || f.alive <= 0) return false;
      const p = worldToClient(f.view.anchor.x, f.view.anchor.z);
      return p !== null && p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1;
    });
    if (inside.length === 0) {
      selection.setSelected(null);
      return;
    }
    if (inside.length === 1) {
      selection.setSelected(inside[0].view.group);
      return;
    }
    selection.setSelectedMany(inside.map((f) => f.view.group));
    hud.showSelection(
      `Selected: <b>${inside.length} fighters</b> — tap ground to order the group.`,
    );
  };

  let selectMode = false;
  const touchControls = new TouchControls(world.renderer.domElement, {
    onTap: handleTap,
    onPan: (dx, dy) => camera.dragPan(dx, dy),
    onPinch: (factor, dx, dy) => {
      camera.zoomBy(factor);
      camera.dragPan(dx, dy);
    },
    onRotate: (dYaw) => camera.rotateBy(dYaw),
    onSelectDrag: paintRubberBand,
    onSelect: runBoxSelect,
  });
  // Select/Pan mode toggle: thumb-reachable on phones (right edge, above
  // the hint card), unobtrusive on desktop. 48px touch target.
  const modeBtn = document.createElement("button");
  modeBtn.id = "select-mode-toggle";
  modeBtn.setAttribute("aria-label", "Toggle drag-select mode");
  const paintModeBtn = (): void => {
    const on = selectMode;
    modeBtn.textContent = on ? "SELECT" : "PAN";
    modeBtn.title = on
      ? "Drag-select mode: drag a box to select fighters (tap to switch back to pan)"
      : "Pan mode: drags move the camera (tap to switch to drag-select)";
    modeBtn.style.cssText =
      "position:fixed;right:12px;bottom:172px;z-index:35;min-width:64px;min-height:48px;" +
      "cursor:pointer;font-size:13px;letter-spacing:1px;border-radius:8px;" +
      `color:${on ? "#e8c67e" : "#e8e2d4"};` +
      `background:${on ? "rgba(60,42,18,0.92)" : "rgba(10,10,12,0.85)"};` +
      `border:2px solid ${on ? "#e8c67e" : "#3a2f22"};`;
  };
  const syncSelectMode = (): void => {
    // Blueprint placement wins: taps place the building, drags pan.
    const active = selectMode && !blueprint;
    touchControls.selectMode = active;
    selection.suspended = active || blueprint !== null;
    paintModeBtn();
  };
  modeBtn.addEventListener("click", () => {
    selectMode = !selectMode;
    syncSelectMode();
  });
  document.body.appendChild(modeBtn);
  const prevOnBlueprint = buildMenu.onBlueprint;
  buildMenu.onBlueprint = (def) => {
    prevOnBlueprint?.(def);
    syncSelectMode();
  };
  syncSelectMode();
  // Desktop: left-drag draws the box in select mode; a plain click keeps
  // the normal click-select (Selection's own mousedown is suspended).
  let mouseBox: { x0: number; y0: number } | null = null;
  world.renderer.domElement.addEventListener("mousedown", (e) => {
    if (e.button !== 0 || !touchControls.selectMode) return;
    if (e.target !== world.renderer.domElement) return;
    mouseBox = { x0: e.clientX, y0: e.clientY };
  });
  window.addEventListener("mousemove", (e) => {
    if (!mouseBox) return;
    paintRubberBand({ x0: mouseBox.x0, y0: mouseBox.y0, x1: e.clientX, y1: e.clientY });
  });
  window.addEventListener("mouseup", (e) => {
    if (!mouseBox) return;
    const r = { x0: mouseBox.x0, y0: mouseBox.y0, x1: e.clientX, y1: e.clientY };
    mouseBox = null;
    paintRubberBand(null);
    if (Math.hypot(r.x1 - r.x0, r.y1 - r.y0) > 12) runBoxSelect(r);
    else selection.selectAt(e.clientX, e.clientY);
  });

  // --- Combat + movement execution ---
  let simNow = 0;
  /** Live-timber count at the last forest-density rebuild (fog of war). */
  let lastLiveTimber = -1;
  /** Signature of the active occluding-building set (fog of war). */
  let lastOccSig = "";
  const retargetAt = new Map<Combatant, number>();

  const nearestEnemy = (c: Combatant): Combatant | null => {
    let best: Combatant | null = null;
    let bestD = Infinity;
    for (const f of fighters) {
      if (f.team === c.team || f.alive <= 0) continue;
      const d = Math.hypot(
        f.view.anchor.x - c.view.anchor.x,
        f.view.anchor.z - c.view.anchor.z,
      );
      if (d < bestD) {
        best = f;
        bestD = d;
      }
    }
    return best;
  };

  const targetAlive = (t: Combatant | BuildingTarget): boolean =>
    t.kind === "building" ? t.building.hp > 0 : t.alive > 0;

  const targetPos = (t: Combatant | BuildingTarget): { x: number; z: number } =>
    t.kind === "building"
      ? { x: t.building.x, z: t.building.z }
      : { ...t.view.anchor };

  const tickCombat = (dt: number): void => {
    // Pairwise melee: each fighter strikes its nearest in-range enemy.
    for (const c of fighters) {
      if (c.alive <= 0 || c.routing) continue;
      c.cooldown = Math.max(0, c.cooldown - dt);
      let foe: Combatant | null = null;
      let foeD = Infinity;
      for (const f of fighters) {
        if (f.team === c.team || f.alive <= 0) continue;
        if (!inMeleeRange(c, f)) continue;
        const d = Math.hypot(
          f.view.anchor.x - c.view.anchor.x,
          f.view.anchor.z - c.view.anchor.z,
        );
        if (d < foeD) {
          foe = f;
          foeD = d;
        }
      }
      let inMelee = false;
      if (foe && c.cooldown <= 0) {
        const kills = dealMeleeDamage(c, foe, dt, simNow);
        if (kills > 0) setFormationCount(foe.view, foe.alive, world.groundY);
        c.cooldown = statNum(c.unit, "attack_interval", 1.5);
        inMelee = true;
      } else if (foe) {
        inMelee = true;
      }
      // Ordered building attacks.
      const orderTarget = c.order?.target;
      if (
        orderTarget?.kind === "building" &&
        targetAlive(orderTarget) &&
        inBuildingRange(c, orderTarget)
      ) {
        if (c.cooldown <= 0) {
          dealBuildingDamage(c, orderTarget, dt);
          c.cooldown = statNum(c.unit, "attack_interval", 1.5);
        }
        inMelee = true;
      }
      // Animation input: rising melee edge starts the pilum throw for
      // pilum-armed formations; the controller then runs their weapon cycle.
      if (inMelee && !c.view.animMelee) noteMeleeContact(c.view);
      tickMorale(c, dt, simNow, inMelee);
      c.view.animMelee = inMelee;
    }
    // Remove the dead; wiping an enemy formation earns a strategic point.
    // Desertion earns nothing — the enemy wasn't beaten, it walked away.
    for (let i = fighters.length - 1; i >= 0; i--) {
      const f = fighters[i];
      if (f.alive <= 0) {
        if (f.team === 1 && !f.deserted) strategicPoints += 1;
        removeFighter(f);
      }
    }
    // Remove destroyed buildings; forums decide the game.
    for (let i = buildings.length - 1; i >= 0; i--) {
      const b = buildings[i];
      if (b.hp <= 0) {
        const isCommand =
          b.def.id === playerCommandId || b.def.id === enemyCommandId;
        removeBuilding(b);
        if (isCommand && b.team === 0) {
          banner.show(
            false,
            `Your ${playerCommandName} has fallen. The ${enemyFaction.name_en} reclaim the valley.`,
          );
        } else if (isCommand && b.team === 1) {
          banner.show(
            true,
            `The enemy camp burns. ${playerFaction.name_en} endures.`,
          );
        }
      }
    }
  };

  /**
   * Separation query radius: covers two large formations marching
   * side-by-side (formation radius tops out near 30u).
   */
  const SEPARATION_QUERY = 48;

  const tickMovement = (dt: number): void => {
    // Formation-vs-formation separation through a spatial hash — one
    // insert per formation, one radius query per marching formation,
    // never a pairwise scan. Only marching anchors are pushed;
    // stationary formations (holding, fighting) keep their ground.
    const sepIndex = new SpatialHash<Combatant>(32);
    for (const c of fighters) {
      if (c.alive <= 0) continue;
      sepIndex.insert(c.view.anchor.x, c.view.anchor.z, c);
    }
    const sepNeighbors: Array<{ x: number; z: number; radius: number }> = [];
    fighters.forEach((c, idx) => {
      if (c.alive <= 0) return;
      const speed = formationSpeed(c.view) * (c.routing ? 1.25 : 1);
      const marchCtx: MarchContext = { nav, team: c.team, simNow, roads };
      if (c.routing) {
        // Flee from the nearest enemy, repathing once a second.
        if (simNow >= (retargetAt.get(c) ?? 0)) {
          retargetAt.set(c, simNow + 1);
          const foe = nearestEnemy(c);
          if (foe) {
            const dx = c.view.anchor.x - foe.view.anchor.x;
            const dz = c.view.anchor.z - foe.view.anchor.z;
            const d = Math.max(Math.hypot(dx, dz), 0.001);
            orderFormationMove(
              c.view,
              c.view.anchor.x + (dx / d) * 30,
              c.view.anchor.z + (dz / d) * 30,
              Math.atan2(dx, dz),
              marchCtx,
              "run", // routers run
            );
          }
        }
      } else if (c.order && targetAlive(c.order.target)) {
        const t = c.order.target;
        const inRange = t.kind === "building" ? inBuildingRange(c, t) : inMeleeRange(c, t);
        if (!inRange && simNow >= (retargetAt.get(c) ?? 0)) {
          retargetAt.set(c, simNow + 0.5);
          const p = targetPos(t);
          orderFormationMove(
            c.view,
            p.x,
            p.z,
            Math.atan2(p.x - c.view.anchor.x, p.z - c.view.anchor.z),
            marchCtx,
            // AI assault waves double-march into contact; everything else
            // advances at the standard march pace.
            c.team === 1 && ai.isCommitted(c) ? "double_march" : "march",
          );
        }
      } else {
        if (c.order && !targetAlive(c.order.target)) c.order = null;
        // Idle AI warbands drift back to their guard posts. Scouts and
        // wave members hold their director orders instead (isCommitted).
        if (c.team === 1 && !ai.isCommitted(c) && simNow >= (retargetAt.get(c) ?? 0)) {
          retargetAt.set(c, simNow + 2);
          const post = ai.guardPost(idx % ai.guardPosts);
          const d = Math.hypot(
            post.x - c.view.anchor.x,
            post.z - c.view.anchor.z,
          );
          if (d > 8) {
            orderFormationMove(
              c.view,
              post.x,
              post.z,
              Math.atan2(post.x - c.view.anchor.x, post.z - c.view.anchor.z),
              marchCtx,
            );
          }
        }
      }
      const m = c.view.march;
      if (m) {
        sepNeighbors.length = 0;
        sepIndex.forEachInRadius(m.ax, m.az, SEPARATION_QUERY, (o) => {
          if (o !== c && o.alive > 0) {
            sepNeighbors.push({
              x: o.view.anchor.x,
              z: o.view.anchor.z,
              radius: o.view.radius,
            });
          }
        });
        // Separation is a positional correction: it must never outrun the
        // formation's own motion in the tick, or clustered warbands get
        // flung around faster than the flow field can pull them forward.
        const push = separationPush(
          m.ax,
          m.az,
          c.view.radius,
          sepNeighbors,
          speed * dt,
        );
        m.ax += push.x;
        m.az += push.z;
      }
      updateFormationMovement(c.view, dt, world.groundY, speed, marchCtx);
    });
  };

  const sim = new SimLoop((dt) => {
    if (banner.visible) return;
    simNow += dt;
    const playerFighters = fighters.filter((f) => f.team === 0);
    const prodMult: Record<string, number> = {};
    const flatIncome: Record<string, number> = {};
    for (const id of db.resources.keys()) {
      prodMult[id] = techFx.gatherMultiplier(id);
      const add = techFx.incomeAdd(id);
      if (add !== 0) flatIncome[id] = add;
    }
    // Formation upkeep (player): assessed per formation on paper strength
    // BEFORE the economy tick so shortfall, morale strain, and desertion
    // are exact for this tick. Civilian (worker) upkeep still flows
    // through economy.tick's extraUpkeep.
    tickFormationUpkeep(
      dt,
      playerFighters,
      0,
      (amount) => {
        const s = economy.stocks.get("food") ?? 0;
        const paid = Math.min(s, amount);
        if (paid > 0) economy.add("food", -paid);
        return paid;
      },
      techFx.upkeepMultiplier(),
    );
    economy.tick(
      dt,
      playerBuildings(),
      workforceUpkeep(workforce.workers),
      prodMult,
      techFx.upkeepMultiplier(),
      flatIncome,
    );

    const doneTech = tickResearch(research, dt, db);
    if (doneTech) {
      hud.showSelection(
        `<b>${doneTech.name_en}</b> researched — ${effectSummary(doneTech)}.`,
      );
      researchPanel.render();
      refreshTrainPanel();
    }

    for (const b of buildings) {
      // The AI builds at its difficulty's construction speed.
      if (tickConstruction(b, dt * (b.team === 1 ? ai.buildSpeedMult : 1))) {
        const v = buildingViews.find((bv) => bv.building === b);
        if (v) updateBuildingView(v);
        syncBuildingTargets();
        hud.showSelection(`<b>${b.def.name_en}</b> completed.`);
      } else if (b.state === "constructing") {
        const v = buildingViews.find((bv) => bv.building === b);
        if (v) updateBuildingView(v);
      }
    }

    for (const done of queues.tick(dt)) {
      const b = buildings.find((x) => x.uid === done.buildingUid);
      const unit = db.units.get(done.unitId);
      if (!b || !unit) continue;
      if (unit.extra["role"] === "worker") {
        const w = workforce.spawn(unit, 0, b.x + 6, b.z + 6);
        workforce.autoAssign(w, workforceCtx());
        hud.showSelection(`<b>${unit.name_en}</b> ready at ${b.def.name_en} — gathering.`);
      } else {
        // Muster tier was locked at enqueue time (what you paid for is
        // what musters); fall back to the smallest musterable tier for
        // orders queued before organization landed.
        const tier = tierById(done.tierId) ?? playerMusterTiers()[0];
        if (!tier) continue;
        const label = `${tier.name_en} ${fighters.filter((f) => f.team === 0).length + 1}`;
        spawnFighter(unit, 0, b.x + 12, b.z + 4, label, tier);
        hud.showSelection(
          `<b>${tier.name_en}</b> (${unit.name_en}) mustered at ${b.def.name_en}.`,
        );
      }
      refreshTrainPanel();
    }

    ai.tick(dt, simNow, {
      fighters,
      buildings,
      playerTargets: buildingTargets.filter((t) => t.team === 0),
    });

    // Render sync for desertion: upkeep ticks (player + AI) shrink
    // formations gradually; the instanced mesh only re-syncs here when the
    // headcount actually changed (combat kills sync at kill time).
    for (const f of fighters) {
      if (f.alive !== f.view.mesh.count) {
        setFormationCount(f.view, f.alive, world.groundY);
      }
    }

    // Day/night + fog of war (vision.update throttles itself to 0.5s sim).
    // Forest density only depends on the SET of live timber nodes (each
    // splats a weight-1 gaussian), so rebuild it only when that set
    // changes — not 30x/sec. Node positions are static; depletion flips
    // amount to 0, which the count catches.
    cycle.tick(dt);
    let liveTimber = 0;
    for (const n of nodes) {
      if (n.kind === "timber" && n.amount > 0) liveTimber++;
    }
    if (liveTimber !== lastLiveTimber) {
      lastLiveTimber = liveTimber;
      vision.setTreeNodes(nodes);
      syncNavForest();
    }
    // Building occluders (UE data `occluder_height`): rebuild only when
    // the active set changes — buildings are static otherwise.
    let occSig = "";
    const occItems: Array<{
      x: number;
      z: number;
      halfExtent: number;
      top: number;
    }> = [];
    for (const b of buildings) {
      if (b.state !== "active") continue;
      const oh = numOpt(b.def.extra["occluder_height"]);
      if (oh === undefined || oh <= 0) continue;
      const [fw, fd] = footprintOf(b.def);
      occSig += `${b.def.id}@${b.x.toFixed(1)},${b.z.toFixed(1)};`;
      occItems.push({
        x: b.x,
        z: b.z,
        halfExtent: Math.hypot(fw, fd) / 2,
        top: world.groundY(b.x, b.z) + oh,
      });
    }
    if (occSig !== lastOccSig) {
      lastOccSig = occSig;
      vision.setOccluders(occItems);
    }
    // Faction-grid vision: stamp units (O(1) each) only when a recompute
    // is due; viewsheds are cast per observer cluster, never per unit.
    // Buildings are few — they keep individual data-driven viewers.
    if (vision.isDue(simNow)) {
      stampUnits();
      vision.update(buildingViewers(), cycle.nightFactor(), simNow);
    }

    tickCombat(dt);
    tickMovement(dt);
    workforce.tick(dt, workforceCtx());
    // Roads: re-sync when buildings change (cheap key check per tick).
    syncRoads();
  });

  new SpeedControl(sim);
  // Pause menu (Esc): Resume / Settings / Quit to menu. Escape while a
  // blueprint is being placed cancels the blueprint instead (build menu).
  const pauseMenu = new PauseMenu({
    onSettings: () => {
      pauseMenu.hide();
      const ps = new SettingsScreen(() => {
        ps.destroy();
        pauseMenu.show();
      });
      ps.show();
    },
    onQuitToMenu: () => window.location.reload(),
  });
  pauseMenu.bind(sim);
  // Capture phase on document: this runs before BuildMenu's own (bubble)
  // Escape handler on window, so a blueprint-cancel Esc never also
  // toggles the pause menu.
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "Escape" || buildMenu.placing) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      pauseMenu.toggle();
    },
    true,
  );
  minimap.bind({
    fighters,
    buildings,
    workers: [...workforce.workers, ...ai.workforce.workers],
    nodes,
    camera,
    vision,
  });

  let last = performance.now();
  let hudTimer = 0;
  let menuTimer = 0;
  let fogTimer = 0;
  // Cumulative environment clock (seconds): Environment.update expects
  // total elapsed time for water/grass animation, not per-frame dt.
  let envElapsed = 0;
  // Rolling FPS estimate for perfProbe (animation perf budget).
  const fpsSamples: number[] = [];
  const fpsEstimate = (): number => {
    if (fpsSamples.length === 0) return 0;
    const sum = fpsSamples.reduce((a, b) => a + b, 0);
    return Math.round((fpsSamples.length / sum) * 100) / 100;
  };
  function frame(now: number): void {
    requestAnimationFrame(frame);
    // Clamp dt to [0, 0.1]: rAF timestamps can run backwards under
    // headless/throttled rendering, and a negative dt would rewind the
    // fog/vision timers (fogTimer must only advance).
    const dt = Math.min(Math.max((now - last) / 1000, 0), 0.1);
    last = now;
    fpsSamples.push(dt);
    if (fpsSamples.length > 90) fpsSamples.shift();
    sim.frame(now);
    camera.update(dt);
    // Environment: day/night sky rig + terrain LOD, grass, water.
    // The sky rig owns the world's lights; the old applyDayNight palette
    // pass is retired (it targeted the placeholder lighting).
    // envElapsed accumulates: Environment.update wants total elapsed
    // seconds for animation phase, not per-frame dt.
    envElapsed += dt;
    world.env.setTimeOfDay(cycle.timeOfDay);
    world.env.update(world.camera, envElapsed);
    world.render();
    hudTimer += dt;
    if (hudTimer > 0.25) {
      hudTimer = 0;
      hud.setCommand(
        commandStatus(
          fighters,
          0,
          queues,
          db,
          playerBuildings().filter((b) => b.state === "active"),
          techFx,
        ),
      );
      // Empire military upkeep: Σ formation upkeep on paper strength.
      hud.setUpkeep(
        fighters
          .filter((f) => f.team === 0 && f.alive > 0)
          .reduce((n, f) => n + formationUpkeepPerMin(f.paperStrength), 0) *
          techFx.upkeepMultiplier(),
      );
      hud.refresh();
      if (selection.hasSelection && !hud.noticeActive()) {
        hud.showStatus(`Selected: ${selectionLabel()}`);
      }
    }
    menuTimer += dt;
    if (menuTimer > 2) {
      menuTimer = 0;
      buildMenu.refresh(economy, playerBuildings(), research.completed);
      refreshTrainPanel();
      researchPanel.render();
      nodeField.sync(nodes);
    }
    workerField.sync(
      [...workforce.workers, ...ai.workforce.workers],
      world.groundY,
      playerTint,
      enemyTint,
      simNow,
      dt,
    );
    // Skeletal animation: formation LOD (skinned near, capsule far) and the
    // shared per-formation animation state (walk/idle/attack).
    {
      const cam = world.camera.position;
      for (const f of fighters) {
        updateFormationLOD(f.view, cam.x, cam.z);
        updateFormationAnim(f.view, dt);
      }
    }
    volley.update(dt);
    // Day/night lighting + HUD clock + torch/brazier visuals.
    const nf = cycle.nightFactor();
    hud.setClock(cycle.clockLabel(), nf > 0.5);
    torchField.sync(fighters, nf, world.groundY);
    brazierLights.sync(buildingViews, nf, world.groundY);
    const foeVisible = (x: number, z: number): boolean =>
      vision.isVisible(0, x, z);
    healthBars.syncFighters(
      fighters,
      world.groundY,
      (f) => foeVisible(f.view.anchor.x, f.view.anchor.z),
    );
    healthBars.syncBuildings(
      buildingViews,
      world.groundY,
      (v) => foeVisible(v.building.x, v.building.z),
    );
    // Fog of war: entity visibility every frame (change-cached), texture
    // upload at ~2Hz after vision recomputes.
    fog.syncEntities({
      fighters,
      buildingViews,
      workerMesh: workerField.mesh,
      workers: [...workforce.workers, ...ai.workforce.workers],
      nodeField,
      nodes,
    });
    fogTimer += dt;
    if (fogTimer > 0.5) {
      fogTimer = 0;
      fog.upload();
    }
    minimap.update(dt);
  }
  hud.refresh();
  buildMenu.refresh(economy, playerBuildings(), research.completed);
  refreshTrainPanel();
  hud.showSelection(
    `Skirmish vs <b>${enemyFaction.name_en}</b> — <b>${DIFFICULTIES[difficulty].label}</b> AI. Raze their ${enemyCommandName}.`,
  );

  // Touch-first devices get touch instructions instead of mouse/keyboard hints.
  if (
    window.matchMedia?.("(pointer: coarse)").matches ||
    "ontouchstart" in window
  ) {
    const hint = document.getElementById("hud-hint");
    if (hint) {
      hint.innerHTML =
        "Tap — select / order<br/>Drag — pan<br/>Pinch — zoom<br/>Twist — rotate<br/>Minimap — jump";
    }
  }

  /**
   * Automation hook for smoke tests (see tools/smoke_mobile.py): exposes
   * selection state, camera rig, and a helper projecting the first player
   * fighter to screen pixels. Not part of the game UI.
   */
  (window as unknown as { __legionnaire: unknown }).__legionnaire = {
    selection,
    camera,
    /** Test hook: the skirmish map size id chosen at faction select. */
    mapSizeId,
    /** Test hook: sim speed (0 while the pause menu is open). */
    simTimeScale: (): number => sim.timeScale,
    /** Test hook: resource node count (validates map-size budgets). */
    debugNodeCount: (): number => nodes.length,
    /**
     * Test hook: environment diagnostics — terrain chunks, water meshes,
     * scatter counts, sky light/fog state (smoke-tests the PCG wiring).
     */
    envDebug: (): {
      seed: string;
      size: number;
      relief: number;
      terrainChunks: number;
      waterMeshes: number;
      scatter: Record<string, number>;
      sunIntensity: number;
      fogNear: number;
      fogFar: number;
    } => {
      const fog = world.scene.fog as { near: number; far: number } | null;
      return {
        seed: world.env.field.seed,
        size: world.env.field.size,
        relief: world.env.field.relief,
        terrainChunks: world.env.terrainChunks,
        waterMeshes: world.env.waterMeshes,
        scatter: { ...world.env.counts } as Record<string, number>,
        sunIntensity: world.env.sky.sun.intensity,
        fogNear: fog?.near ?? -1,
        fogFar: fog?.far ?? -1,
      };
    },
    /** Test hook: AI director state (workers, warbands, waves launched). */
    debugAi: (): { workers: number; warbands: number; wavesLaunched: number } => {
      const s = ai.debugState();
      return { workers: s.workers, warbands: s.warbands, wavesLaunched: s.wavesLaunched };
    },
    /** Test hook: visibility texture summary (fog-of-war diagnostics).
     * Counts texels with any visibility (R>0) or explored flag (G>0).
     */
    debugVisibility: (): { visible: number; explored: number; total: number } => {
      const data = world.visibilityTex.image.data as unknown as Uint8Array;
      let visible = 0, explored = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] > 0) visible++;
        if (data[i + 1] > 0) explored++;
      }
      return { visible, explored, total: data.length / 4 };
    },
    hudText: () => document.getElementById("hud-bottom")?.textContent ?? "",
    firstFighterWorld: (): { x: number; z: number } | null => {
      const f = fighters.find((c) => c.team === 0 && c.alive > 0);
      return f ? { x: f.view.anchor.x, z: f.view.anchor.z } : null;
    },
    /**
     * Medallion diagnostics (smoke tests): cached portrait keys, unit
     * types whose intro has played, and scratch-GL availability.
     */
    medallionDebug: (): {
      cached: string[];
      introsPlayed: string[];
      webgl: boolean;
    } => ({
      cached: medallions.cachedKeys(),
      introsPlayed: medallions.introsPlayed(),
      webgl: medallions.webglAvailable(),
    }),
    /** Test hook: clear the selection (medallion replay checks). */
    debugDeselect: (): void => {
      selection.setSelected(null);
    },
    /**
     * Test hook: team-0 fighters' organization state — tier, paper vs
     * field strength, upkeep arrears and morale (smoke-tests musters).
     */
    debugFighters: (): Array<{
      tierId: string;
      paperStrength: number;
      fieldBodies: number;
      alive: number;
      morale: number;
      routing: boolean;
      supplyDebt: number;
    }> =>
      fighters
        .filter((f) => f.team === 0 && f.alive > 0)
        .map((f) => ({
          tierId: f.tierId,
          paperStrength: f.paperStrength,
          // Field bodies at muster (maxAlive); alive shrinks with casualties.
          fieldBodies: f.maxAlive,
          alive: f.alive,
          morale: Math.round(f.morale),
          routing: f.routing,
          supplyDebt: Math.round(f.supplyDebt * 10) / 10,
        })),
    /** Diagnostic: name of the selectable under client coords (or "null"). */
    debugPick: (clientX: number, clientY: number): string => {
      return pickSelectable(clientX, clientY)?.name ?? "null";
    },
    /**
     * Test hook: spawn `n` team-0 fighters of the given muster tier
     * (default: smallest) in a grid near the player's base
     * (perf/animation smoke tests). Optional `formationId` overrides the
     * default formation def (e.g. "testudo" for defensive-stance checks).
     */
    debugSpawnFighters: (
      n: number,
      tierId?: string,
      unitId?: string,
      formationId?: string,
    ): number => {
      const warrior =
        (unitId ? db.units.get(unitId) : undefined) ??
        [...db.units.values()].find(
          (u) => u.faction === playerFaction.id && consumesCommand(u),
        );
      const tiers = playerMusterTiers();
      const tier = (tierId ? tiers.find((t) => t.id === tierId) : undefined)
        ?? tiers[0];
      if (!warrior || !tier) return 0;
      const base = buildings.find((b) => b.team === 0) ?? { x: 0, z: 0 };
      const per = Math.ceil(Math.sqrt(n));
      const fdef =
        (formationId ? db.formations.get(formationId) : undefined) ??
        formationDef;
      let spawned = 0;
      for (let i = 0; i < n; i++) {
        const gx = (i % per) - per / 2;
        const gz = Math.floor(i / per) - per / 2;
        spawnFighter(
          warrior,
          0,
          base.x + 60 + gx * 30,
          base.z + gz * 30,
          `Perf ${i}`,
          tier,
          fdef,
        );
        spawned++;
      }
      return spawned;
    },
    /**
     * Test hook: place a NON-instant team-0 building (construction with
     * assigned faber builders — animation smoke tests).
     */
    debugConstructBuilding: (defId: string): boolean => {
      const def = db.buildings.get(defId);
      if (!def) return false;
      const anchor = buildings.find((b) => b.team === 0) ?? { x: 0, z: 0 };
      addBuilding(def, anchor.x + 25, anchor.z + 25, false, 0);
      return true;
    },
    /** Test hook: move the camera (LOD comparisons in perf tests). */
    debugCamera: (x: number, y: number, z: number): void => {
      // Drive the camera controller (it recomputes position every frame),
      // not the raw camera position.
      camera.target.set(x, 0, z);
      camera.distance = Math.min(320, Math.max(18, y * 1.4));
    },
    /**
     * Animation diagnostics: current activities per worker state and per
     * formation, plus clip sources (procedural vs real GLB).
     */
    animProbe: (): {
      workers: Record<string, number>;
      formations: Array<{ label: string; activity: string; lodNear: boolean }>;
      clips: Record<string, string>;
    } => {
      const workers: Record<string, number> = {};
      for (const w of [...workforce.workers, ...ai.workforce.workers]) {
        const key = `${w.state}`;
        workers[key] = (workers[key] ?? 0) + 1;
      }
      return {
        workers,
        formations: fighters.map((f) => ({
          label: f.view.label,
          activity: f.view.anim?.activity() ?? "none",
          lodNear: f.view.lodNear,
          pace: f.view.animPace,
          defensive: f.view.animDefensive,
          roadColumn: f.view.roadColumn,
        })),
        clips: animSys.clipSources(),
      };
    },
    /**
     * Render perf diagnostics: draw calls / triangles from the last frame
     * plus an FPS estimate over ~60 frames.
     */
    perfProbe: (): {
      calls: number;
      triangles: number;
      fps: number;
    } => {
      const info = world.renderer.info.render;
      return {
        calls: info.calls,
        triangles: info.triangles,
        fps: fpsEstimate(),
      };
    },
    /** Name of the currently selected object (or "null"). */
    selectedName: (): string => selection.selected?.name ?? "null",
    /** Test hook: box-selected group size (touch select mode). */
    selectionCount: (): number => selection.selectedMany.length,
    /** Building states for smoke tests: team-0 buildings. */
    buildingStates: (): Array<{
      id: string;
      x: number;
      z: number;
      progress: number;
      hp: number;
      maxHp: number;
    }> =>
      buildingViews
        .filter((v) => v.building.team === 0)
        .map((v) => ({
          id: v.building.def.id,
          x: Math.round(v.building.x * 10) / 10,
          z: Math.round(v.building.z * 10) / 10,
          progress: Math.round(v.building.progress * 100) / 100,
          hp: Math.round(v.building.hp),
          maxHp: v.building.maxHp,
        })),
    /** Art-map diagnostics: resolved model descriptors (JSON-safe). */
    artModelForBuilding: (id: string): unknown =>
      JSON.parse(JSON.stringify(db.buildingModel(id, echelonState.index))),
    artModelForUnit: (id: string): unknown =>
      JSON.parse(JSON.stringify(db.unitModel(id))),
    /** Faber proof: mapping entry incl. the AN_faber_* animation set. */
    faberArt: (): unknown =>
      JSON.parse(JSON.stringify(db.unitModel("faber"))),
    /** Test hook: mark a tech researched (smoke-tests the tech chain). */
    debugCompleteTech: (id: string): void => {
      research.completed.add(id);
      buildMenu.refresh(economy, playerBuildings(), research.completed);
    },
    /** Test hook: add resources (smoke-tests that need training funds). */
    debugAddStock: (id: string, n: number): void => {
      economy.add(id, n);
    },
    /** Test hook: select a team-0 building by def id (opens its panels). */
    debugSelectBuilding: (defId: string): boolean => {
      const view = buildingViews.find(
        (v) => v.building.team === 0 && v.building.def.id === defId,
      );
      if (!view) return false;
      selection.setSelected(view.group);
      return true;
    },
    /**
     * Test hook: instantly place an active team-0 building of the given
     * def next to the player's command building (smoke-tests that need
     * e.g. a barracks without walking the build menu). Optional dx/dz
     * offset from the anchor (e.g. laying a line of road buildings).
     */
    debugPlaceBuilding: (defId: string, dx = 25, dz = 25): boolean => {
      const def = db.buildings.get(defId);
      if (!def) return false;
      const anchor = buildings.find((b) => b.team === 0) ?? {
        x: 0,
        z: 0,
      };
      addBuilding(def, anchor.x + dx, anchor.z + dz, true, 0);
      return true;
    },
    /** Test hook: the player's command pool (used/total formation slots). */
    debugCommandStatus: (): { used: number; total: number } =>
      commandStatus(
        fighters,
        0,
        queues,
        db,
        playerBuildings().filter((b) => b.state === "active"),
        techFx,
      ),
    /**
     * Test hook: the player's musterable organization tiers right now —
     * id, paper strength, field bodies, formation upkeep (food/min).
     */
    orgInfo: (): Array<{
      tierId: string;
      name: string;
      paperStrength: number;
      fieldBodies: number;
      upkeepFoodPerMin: number;
    }> =>
      playerMusterTiers().map((t) => ({
        tierId: t.id,
        name: t.name_en,
        paperStrength: t.paperStrength,
        fieldBodies: t.fieldBodies,
        upkeepFoodPerMin: formationUpkeepPerMin(t.paperStrength),
      })),
    /**
     * Test hook: train the first trainable military unit at the given
     * barracks at the requested tier (default: smallest musterable tier),
     * paying tier-scaled cost. Returns the mustered tier id, or null.
     */
    debugTrainMilitary: (defId: string, tierId?: string, unitId?: string): string | null => {
      const b = buildingViews.find(
        (v) => v.building.team === 0 && v.building.def.id === defId,
      )?.building;
      if (!b) return null;
      const trainable = trainableUnits(b, db).filter((u) => consumesCommand(u));
      const unit = (unitId ? trainable.find((u) => u.id === unitId) : undefined) ?? trainable[0];
      if (!unit) return null;
      const tiers = playerMusterTiers();
      const tier = (tierId ? tierById(tierId) : undefined) ?? tiers[0] ?? null;
      if (!tier) return null;
      const cost = tierTrainCost(scaledTrainCost(unit), tier);
      if (!economy.pay(cost)) return null;
      queues.enqueue(b.uid, unit, tier.id, trainScale(tier));
      return tier.id;
    },
    visibilityAt: (x: number, z: number): number => vision.visibilityAt(0, x, z),
    exploredAt: (x: number, z: number): boolean => vision.exploredAt(0, x, z),
    enemyVisibleAt: (x: number, z: number): boolean =>
      vision.isVisible(0, x, z),
    timeOfDay: (): number => cycle.timeOfDay,
    nightFactor: (): number => cycle.nightFactor(),
    setTimeOfDay: (t: number): void => {
      cycle.restore(t);
    },
    /** Fog texture diagnostics: nonzero texels in vision vs GPU texture. */
    fogProbe: (): { visionTexels: number; gpuTexels: number } => {
      const vd = vision.textureData();
      const td = world.visibilityTex.image.data as unknown as Uint8Array;
      let v = 0;
      let t = 0;
      for (let i = 0; i < vd.length; i += 4) {
        if (vd[i] > 0 || vd[i + 1] > 0) v++;
      }
      for (let i = 0; i < td.length; i += 4) {
        if (td[i] > 0 || td[i + 1] > 0) t++;
      }
      return { visionTexels: v, gpuTexels: t };
    },
    /** Actual first soldier position (not the anchor, which retargets). */
    firstSoldierPos: (): { x: number; z: number } | null => {
      const f = fighters.find((c) => c.team === 0 && c.alive > 0);
      const pos = f?.view.positions[0];
      return pos ? { x: pos.x, z: pos.z } : null;
    },
    /** AI director state for behavior observation. */
    aiDebug: (): Record<string, unknown> => {
      const forum = buildings.find(
        (b) => b.def.id === playerCommandId && b.team === 0,
      );
      const dbg = ai.debugState();
      return {
        ...dbg,
        assaultLaunched: dbg.assaultsLaunched > 0,
        warbands: fighters
          .filter((c) => c.team === 1)
          .map((c) => ({
            label: c.view.label,
            x: c.view.anchor.x,
            z: c.view.anchor.z,
            alive: c.alive,
          })),
        forum: forum ? { x: forum.x, z: forum.z, hp: forum.hp } : null,
      };
    },
    firstFighterScreenPos: (): { x: number; y: number } | null => {
      const f = fighters.find((c) => c.team === 0 && c.alive > 0);
      if (!f) return null;
      const rect = world.renderer.domElement.getBoundingClientRect();
      const v = new THREE.Vector3(
        f.view.anchor.x,
        world.groundY(f.view.anchor.x, f.view.anchor.z),
        f.view.anchor.z,
      ).project(world.camera);
      if (v.z > 1) return null;
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },
    /** Test hook: screen positions of all live team-0 fighters. */
    fighterScreenPositions: (): Array<{ x: number; y: number }> => {
      const rect = world.renderer.domElement.getBoundingClientRect();
      const out: Array<{ x: number; y: number }> = [];
      for (const f of fighters) {
        if (f.team !== 0 || f.alive <= 0) continue;
        const v = new THREE.Vector3(
          f.view.anchor.x,
          world.groundY(f.view.anchor.x, f.view.anchor.z),
          f.view.anchor.z,
        ).project(world.camera);
        if (v.z > 1) continue;
        out.push({
          x: rect.left + ((v.x + 1) / 2) * rect.width,
          y: rect.top + ((1 - v.y) / 2) * rect.height,
        });
      }
      return out;
    },
    /** Test hook: select the first live team-0 fighter. */
    debugSelectFighter: (): boolean => {
      const f = fighters.find((c) => c.team === 0 && c.alive > 0);
      if (!f) return false;
      selection.setSelected(f.view.group);
      return true;
    },
    /** Test hook: order the selected fighter to a world point (flow field). */
    debugOrderSelected: (x: number, z: number): boolean => {
      const sel = selection.selected;
      const fighter = sel?.userData.fighter as Combatant | undefined;
      if (!fighter || fighter.team !== 0 || fighter.alive <= 0) return false;
      fighter.order = null;
      orderFormationMove(
        fighter.view,
        x,
        z,
        Math.atan2(x - fighter.view.anchor.x, z - fighter.view.anchor.z),
        { nav, team: 0, simNow, roads },
      );
      return true;
    },
    /**
     * Test hook: labels of team-0 fighters in the order debugOrderFighter
     * / debugMelee index into (road/animation smoke tests).
     */
    debugTeam0Labels: (): string[] =>
      fighters.filter((c) => c.team === 0 && c.alive > 0).map((f) => f.view.label),
    /**
     * Test hook: order fighter `index` (team-0 order) to a world point at
     * an explicit march pace (road smoke tests).
     */
    debugOrderFighter: (
      index: number,
      x: number,
      z: number,
      pace: "march" | "double_march" | "run" = "march",
    ): boolean => {
      const team0 = fighters.filter((c) => c.team === 0 && c.alive > 0);
      const fighter = team0[index];
      if (!fighter) return false;
      fighter.order = null;
      orderFormationMove(
        fighter.view,
        x,
        z,
        Math.atan2(x - fighter.view.anchor.x, z - fighter.view.anchor.z),
        { nav, team: 0, simNow, roads },
        pace,
      );
      return true;
    },
    /**
     * Test hook: inject map-generator road rects, bypassing buildings
     * (road smoke tests). Locks syncRoads for the session.
     */
    debugFakeRoads: (
      rects: Array<{
        x: number;
        z: number;
        hw: number;
        hd: number;
        moveBonus: number;
        haulBonus: number;
      }>,
    ): boolean => {
      roadsLocked = true;
      roads.setRoads(rects);
      nav.setRoads(rects);
      return true;
    },
    /**
     * Test hook: spawn one team-1 fighter at a world position (melee
     * animation smoke: real combat loop drives the pilum throw -> volley).
     */
    debugSpawnFoe: (unitId: string, x: number, z: number): boolean => {
      const unit = db.units.get(unitId);
      const tiers = playerMusterTiers();
      if (!unit || !tiers[0]) return false;
      spawnFighter(unit, 1, x, z, `Foe ${unitId}`, tiers[0]);
      return true;
    },
    /** Current sim clock (smoke-test probe). */
    simNow: (): number => simNow,
    /** Live pilum-volley tracer count (presentation). */
    volleyProbe: (): number => volley.active,
    /** Team-0 fighter anchor positions (smoke-test probe). */
    debugTeam0Anchors: (): Array<{
      label: string;
      x: number;
      z: number;
      mx: number | null;
      mz: number | null;
    }> =>
      fighters
        .filter((c) => c.team === 0 && c.alive > 0)
        .map((f) => ({
          label: f.view.label,
          x: Math.round(f.view.anchor.x * 10) / 10,
          z: Math.round(f.view.anchor.z * 10) / 10,
          mx: f.view.march ? Math.round(f.view.march.ax * 10) / 10 : null,
          mz: f.view.march ? Math.round(f.view.march.az * 10) / 10 : null,
        })),
    /** Active road rects (smoke-test probe). */
    roadsProbe: (): ReadonlyArray<{
      x: number;
      z: number;
      hw: number;
      hd: number;
      moveBonus: number;
    }> => roads.list(),
    /**
     * Road diagnostics: per-formation road state (4-abreast column check),
     * speed bonus, and pace.
     */
    roadProbe: (): Array<{
      label: string;
      onRoad: boolean;
      bonus: number;
      slotCols: number;
      speed: number;
      pace: string;
      defensive: boolean;
    }> =>
      fighters.map((f) => {
        let maxCol = 0;
        const cols = new Map<number, number>();
        for (const s of f.view.slots) {
          const k = Math.round(s.z * 100);
          cols.set(k, (cols.get(k) ?? 0) + 1);
        }
        for (const n of cols.values()) maxCol = Math.max(maxCol, n);
        return {
          label: f.view.label,
          onRoad: f.view.roadColumn,
          bonus: Math.round(f.view.roadBonus * 1000) / 1000,
          slotCols: maxCol,
          speed: Math.round(formationSpeed(f.view) * 100) / 100,
          pace: f.view.animPace,
          defensive: f.view.animDefensive,
        };
      }),
    /**
     * GLB drop-in proof (throwaway-model validation). Reports ArtMap
     * availability for the probe units/buildings plus whether vendored
     * geometry actually replaced placeholders in the live scene.
     */
    debugGlbProof: (): {
      forumAvailable: boolean;
      forumWebPath: string | null;
      forumLodTiers: number;
      faberAvailable: boolean;
      hastatiAvailable: boolean;
      buildingRealModels: number;
      formationRealGeometry: boolean;
      formationLodLoaded: boolean;
      formationFarLodActive: boolean;
    } => {
      const forumDef = db.buildings.get("forum");
      const faberDef = db.units.get("faber");
      const hastatiDef = db.units.get("hastati");
      const fm = forumDef ? db.buildingModel(forumDef.id, 1) : null;
      return {
        forumAvailable: fm?.available ?? false,
        forumWebPath: fm?.webPath ?? null,
        forumLodTiers: fm?.lodPaths.length ?? 0,
        faberAvailable: faberDef ? db.unitModel(faberDef.id).available : false,
        hastatiAvailable: hastatiDef ? db.unitModel(hastatiDef.id).available : false,
        buildingRealModels: buildingViews.filter((v) => v.model !== null).length,
        formationRealGeometry: fighters.some((f) => f.view.realGeometry),
        formationLodLoaded: fighters.some((f) => f.view.unitLodGeo !== null),
        formationFarLodActive: fighters.some(
          (f) => f.view.unitLodGeo !== null && f.view.mesh.geometry === f.view.unitLodGeo,
        ),
      };
    },
    /** March diagnostics for the first team-0 fighter (flow-field smoke). */    marchInfo: (): {
      marching: boolean;
      anchorX: number;
      anchorZ: number;
      distToGoal: number;
      fields: number;
    } | null => {
      const f = fighters.find((c) => c.team === 0 && c.alive > 0);
      if (!f) return null;
      const m = f.view.march;
      return {
        marching: m !== null,
        anchorX: Math.round(f.view.anchor.x * 10) / 10,
        anchorZ: Math.round(f.view.anchor.z * 10) / 10,
        distToGoal: m
          ? Math.round(nav.distanceTo(m.field, f.view.anchor.x, f.view.anchor.z) * 10) / 10
          : -1,
        fields: nav.fieldCount,
      };
    },
    /** Banner (victory/defeat) state for acceptance tests. */
    bannerInfo: (): { visible: boolean; victory: boolean | null } => ({
      visible: banner.visible,
      victory: banner.visible ? banner.victory : null,
    }),
    /** Economy stocks for acceptance tests (income verification). */
    economyProbe: (): Record<string, number> =>
      Object.fromEntries(economy.stocks),
    /** Enemy buildings: hp tracking for acceptance tests. */
    enemyBuildings: (): Array<{
      id: string;
      hp: number;
      maxHp: number;
      x: number;
      z: number;
    }> =>
      buildings
        .filter((b) => b.team === 1)
        .map((b) => ({
          id: b.def.id,
          hp: Math.round(b.hp),
          maxHp: b.maxHp,
          x: Math.round(b.x * 10) / 10,
          z: Math.round(b.z * 10) / 10,
        })),
    /**
     * Test hook: set a building's hp to a fraction of max (acceptance
     * tests: repair targets and accelerating victory/defeat runs; the
     * banner itself still fires through the real destruction path).
     */
    debugDamageBuilding: (
      team: 0 | 1,
      defId: string,
      frac: number,
    ): boolean => {
      const b = buildings.find((x) => x.team === team && x.def.id === defId);
      if (!b) return false;
      b.hp = Math.max(1, b.maxHp * frac);
      return true;
    },
    /**
     * Test hook: order a fighter (by per-team index) to attack a
     * building (acceptance tests: real building-damage path).
     */
    debugOrderAttackBuilding: (
      index: number,
      defTeam: 0 | 1,
      defId: string,
      attackerTeam: 0 | 1 = 0,
    ): boolean => {
      const fighter = fighters.filter(
        (c) => c.team === attackerTeam && c.alive > 0,
      )[index];
      const b = buildings.find((x) => x.team === defTeam && x.def.id === defId);
      if (!fighter || !b) return false;
      const target = buildingTargets.find((t) => t.building === b);
      if (!target) return false;
      fighter.order = { target };
      return true;
    },
    /**
     * Test hook: labels of team-1 fighters (acceptance defeat runs).
     */
    debugTeam1Labels: (): string[] =>
      fighters.filter((c) => c.team === 1 && c.alive > 0).map((f) => f.view.label),
    /**
     * Test hook: assign the nearest team-0 worker to farm hands
     * (acceptance tests). Mirrors the real right-click order
     * (`issueWorkerOrder` -> workforce.assignFarm), which reassigns the
     * selected worker regardless of its current state.
     */
    debugAssignFarmWorker: (defId: string): boolean => {
      const farm = buildings.find(
        (b) => b.team === 0 && b.def.id === defId && b.state === "active",
      );
      if (!farm) return false;
      const cands = workforce.workers
        .filter((w) => w.team === 0)
        .sort(
          (a, b) =>
            Math.hypot(a.x - farm.x, a.z - farm.z) -
            Math.hypot(b.x - farm.x, b.z - farm.z),
        );
      const w = cands[0];
      if (!w) return false;
      workforce.assignFarm(w, farm);
      return true;
    },
    /**
     * Test hook: screen position of the index-th live team-0 worker
     * (true-input worker orders).
     */
    debugWorkerScreenPos: (
      index = 0,
    ): { x: number; y: number } | null => {
      const w = workforce.workers.filter((x) => x.team === 0)[index];
      if (!w) return null;
      const rect = world.renderer.domElement.getBoundingClientRect();
      const v = new THREE.Vector3(
        w.x,
        world.groundY(w.x, w.z) + 1,
        w.z,
      ).project(world.camera);
      if (v.z > 1) return null;
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },
    /**
     * Test hook: screen position of the nearest resource node
     * (optionally of the given kind) to the player's base
     * (true-input worker orders).
     */
    debugNodeScreenPos: (
      kind?: string,
    ): { x: number; y: number } | null => {
      const base = buildings.find((b) => b.team === 0);
      const cands = nodes
        .filter((n) => n.amount > 0 && (!kind || n.kind === kind))
        .sort(
          (a, b) =>
            Math.hypot(a.x - (base?.x ?? 0), a.z - (base?.z ?? 0)) -
            Math.hypot(b.x - (base?.x ?? 0), b.z - (base?.z ?? 0)),
        );
      const n = cands[0];
      if (!n) return null;
      const rect = world.renderer.domElement.getBoundingClientRect();
      const v = new THREE.Vector3(
        n.x,
        world.groundY(n.x, n.z) + 1,
        n.z,
      ).project(world.camera);
      if (v.z > 1) return null;
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },
    /**
     * Test hook: assign repair crews to a team-0 building
     * (acceptance tests: real repair cycle).
     */
    debugOrderRepair: (defId: string): boolean => {
      const b = buildings.find(
        (x) => x.team === 0 && x.def.id === defId && x.state === "active",
      );
      if (!b) return false;
      workforce.assignRepairers(b, 2, 0);
      return true;
    },
    /** Test hook: screen position of a building (true-input right-clicks). */
    buildingScreenPos: (
      defId: string,
      team: 0 | 1 = 0,
    ): { x: number; y: number } | null => {
      const b = buildings.find((x) => x.team === team && x.def.id === defId);
      if (!b) return null;
      const rect = world.renderer.domElement.getBoundingClientRect();
      const v = new THREE.Vector3(
        b.x,
        world.groundY(b.x, b.z),
        b.z,
      ).project(world.camera);
      if (v.z > 1) return null;
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },
  };

  requestAnimationFrame(frame);
}

boot().catch((e) => {
  document.getElementById("hud-bottom")!.textContent =
    `Failed to start: ${String(e)}`;
  console.error(e);
});
